import { essentialUsefulInfoText } from "./usefulInfoPresentation";
import { roundCurrency, computeSplit } from "./expenseMath";
import { normalizeGuidedPlan, type GuidedPlan } from "./guidedPlan";
import { normalizeTripSetup, type TripSetup } from "./tripSetup";
import { queueExpenseForFainanceShareSync, queueTripForFainanceShareSync } from "../integrations/fainanceLinkService";
export type TripStatus = "planning" | "upcoming" | "active" | "completed";

export interface TripSummary {
  id: string;
  ownerUid: string;
  name: string;
  destination: string;
  startDate: string;
  endDate: string;
  status: TripStatus;
  profilePhotoGooglePlaceId?: string;
  profilePhotoIndex?: number;
  profilePhotoDataUrl?: string;
  coverPhotoGooglePlaceId?: string;
  coverPhotoIndex?: number;
  coverPhotoDataUrl?: string;
  aiPhotosSelectedAt?: string;
  archivedAt?: string;
  deletedAt?: string | null;
  trashDaysLeft?: number;
  memberRole?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTripInput {
  name: string;
  destination: string;
  startDate: string;
  endDate: string;
}

const STORAGE_VERSION = 1;

function tripsKey(uid: string): string {
  return `tripai.trips.v${STORAGE_VERSION}.${uid}`;
}


function emitTripsUpdated(uid: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("tripai:trips-updated", { detail: { uid } }));
  window.dispatchEvent(new CustomEvent("tripai:gamification-source-updated", { detail: { uid } }));
}

function selectedTripKey(uid: string): string {
  return `tripai.selectedTrip.v${STORAGE_VERSION}.${uid}`;
}

function normalizeTrip(value: unknown, uid: string): TripSummary | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<TripSummary>;
  if (!candidate.id || !candidate.name) return null;
  return {
    id: String(candidate.id),
    ownerUid: String(candidate.ownerUid || uid),
    name: String(candidate.name),
    destination: String(candidate.destination || ""),
    startDate: String(candidate.startDate || ""),
    endDate: String(candidate.endDate || ""),
    status: candidate.status === "upcoming" || candidate.status === "active" || candidate.status === "completed"
      ? candidate.status
      : "planning",
    profilePhotoGooglePlaceId: String(candidate.profilePhotoGooglePlaceId || "") || undefined,
    profilePhotoIndex: Number.isFinite(Number(candidate.profilePhotoIndex)) ? Math.max(0, Number(candidate.profilePhotoIndex)) : undefined,
    profilePhotoDataUrl: String(candidate.profilePhotoDataUrl || "") || undefined,
    coverPhotoGooglePlaceId: String(candidate.coverPhotoGooglePlaceId || "") || undefined,
    coverPhotoIndex: Number.isFinite(Number(candidate.coverPhotoIndex)) ? Math.max(0, Number(candidate.coverPhotoIndex)) : undefined,
    coverPhotoDataUrl: String(candidate.coverPhotoDataUrl || "") || undefined,
    aiPhotosSelectedAt: String(candidate.aiPhotosSelectedAt || "") || undefined,
    archivedAt: String(candidate.archivedAt || "") || undefined,
    deletedAt: candidate.deletedAt === null ? null : (String(candidate.deletedAt || "") || undefined),
    memberRole: String(candidate.memberRole || "") || undefined,
    createdAt: String(candidate.createdAt || new Date().toISOString()),
    updatedAt: String(candidate.updatedAt || candidate.createdAt || new Date().toISOString())
  };
}

function sortTrips(items: TripSummary[]): TripSummary[] {
  return [...items].sort((a, b) => {
    if (a.startDate && b.startDate && a.startDate !== b.startDate) return a.startDate.localeCompare(b.startDate);
    if (a.startDate && !b.startDate) return -1;
    if (!a.startDate && b.startDate) return 1;
    return b.updatedAt.localeCompare(a.updatedAt);
  });
}

let tripReadCache: { uid: string; raw: string; items: TripSummary[] } | undefined;

function readTripsAll(uid: string): TripSummary[] {
  try {
    const raw = localStorage.getItem(tripsKey(uid));
    if (!raw) { tripReadCache = undefined; return []; }
    if (tripReadCache?.uid === uid && tripReadCache.raw === raw) {
      return tripReadCache.items.map((item) => ({ ...item }));
    }
    tripReadCache = undefined;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const items = sortTrips(parsed.map((item) => normalizeTrip(item, uid)).filter((item): item is TripSummary => Boolean(item)));
    // Keep legacy generated timestamps unchanged; bound retained data to one account.
    const stableDates = parsed.every((item) => !item || typeof item !== "object" || !item.id || !item.name || Boolean(item.createdAt));
    if (stableDates && raw.length <= 2_000_000) tripReadCache = { uid, raw, items };
    return items.map((item) => ({ ...item }));
  } catch {
    return [];
  }
}

function writeTripsAll(uid: string, items: TripSummary[]): void {
  localStorage.setItem(tripsKey(uid), JSON.stringify(sortTrips(items)));
}

function isArchivedTripLocal(uid: string, tripId: string): boolean {
  return Boolean(readTripsAll(uid).find((trip) => trip.id === tripId)?.archivedAt);
}

function notifyArchivedMutationBlocked(uid: string, tripId: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("tripai:archived-mutation-blocked", { detail: { uid, tripId } }));
}

function blockArchivedMutation(uid: string, tripId: string): boolean {
  const blocked = isArchivedTripLocal(uid, tripId);
  if (blocked) notifyArchivedMutationBlocked(uid, tripId);
  return blocked;
}

export function listTrips(uid: string): TripSummary[] {
  return readTripsAll(uid).filter((trip) => !trip.deletedAt);
}

/** Synchronize the phone's local trip list only from a server-verified snapshot.
 * Shared trips are outside the scope of personal JSON transfers.
 * This does not enqueue any sync writes back to Firebase/fAInance. */
export function replaceOwnedTripSummariesFromBackup(uid: string, snapshots: TripSummary[], verifiedCount: number): void {
  if (!Array.isArray(snapshots) || !Number.isSafeInteger(verifiedCount) ||
      verifiedCount < 0 || snapshots.length !== verifiedCount) throw new Error("BACKUP_RESULT_VERIFICATION_FAILED");
  const current = readTripsAll(uid);
  const seen = new Set<string>();
  const localOwned = new Map(current.filter((trip) => trip.ownerUid === uid).map((trip) => [trip.id, trip]));
  const restored: TripSummary[] = [];
  for (const item of snapshots) {
    if (!item || !item.id || item.ownerUid !== uid || seen.has(item.id) || !item.name) throw new Error("BACKUP_RESULT_VERIFICATION_FAILED");
    seen.add(item.id);
    const old = localOwned.get(item.id);
    const clean = normalizeTrip(item, uid);
    if (!clean) throw new Error("BACKUP_RESULT_VERIFICATION_FAILED");
    // The status callable intentionally omits oversized inline images: preserve
    // known local pictures until the normal cloud listener hydrates them.
    if (old) for (const field of ["coverPhotoDataUrl", "profilePhotoDataUrl"] as const) {
      if (!clean[field] && old[field]) clean[field] = old[field];
    }
    restored.push(clean);
  }
  const shared = current.filter((trip) => trip.ownerUid !== uid);
  writeTripsAll(uid, [...shared, ...restored]);
  emitTripsUpdated(uid);
}

/** Reconcile a complete, authoritative owned-trip snapshot. Keep removed local
 * summaries recoverable and retain their workspace; pending local creations stay visible. */
export function reconcileOwnedTripSummariesFromServer(uid: string, snapshots: TripSummary[], pendingIds: string[]): void {
  if (!Array.isArray(snapshots) || !Array.isArray(pendingIds)) throw new Error("INVALID_TRIP_SNAPSHOT");
  const seen = new Set<string>();
  for (const item of snapshots) {
    if (!item || !item.id || !item.name || item.ownerUid !== uid || seen.has(item.id)) throw new Error("INVALID_TRIP_SNAPSHOT");
    seen.add(item.id);
  }
  const current = readTripsAll(uid);
  const pending = new Set(pendingIds);
  const removed = current.filter(item => item.ownerUid === uid && !seen.has(item.id) && !pending.has(item.id));
  if (removed.length) {
    const key = `tripai.tripSummarySync.quarantine.v1.${uid}`;
    const raw = localStorage.getItem(key);
    const saved = raw ? JSON.parse(raw) : {};
    if (!saved || typeof saved !== "object" || Array.isArray(saved)) throw new Error("INVALID_TRIP_QUARANTINE");
    for (const trip of removed) saved[trip.id] = { trip, quarantinedAt: new Date().toISOString(), reason: "absent-from-server" };
    // If saving fails, do not hide any local data.
    localStorage.setItem(key, JSON.stringify(saved));
  }
  const preservedPending = current.filter(item => item.ownerUid === uid && pending.has(item.id) && !seen.has(item.id));
  replaceOwnedTripSummariesFromBackup(uid, [...snapshots, ...preservedPending], snapshots.length + preservedPending.length);
  const selected = getSelectedTripId(uid);
  if (selected && removed.some(item => item.id === selected)) setSelectedTripId(uid, null);
}

export function listDeletedTrips(uid: string): TripSummary[] {
  return readTripsAll(uid)
    .filter((trip) => Boolean(trip.deletedAt))
    .map((trip) => ({ ...trip, trashDaysLeft: tripTrashDaysLeft(trip) }))
    .sort((a, b) => String(b.deletedAt || "").localeCompare(String(a.deletedAt || "")));
}

export function createTrip(uid: string, input: CreateTripInput): TripSummary {
  const now = new Date().toISOString();
  const id = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `trip_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const trip: TripSummary = {
    id,
    ownerUid: uid,
    name: input.name.trim(),
    destination: input.destination.trim(),
    startDate: input.startDate,
    endDate: input.endDate,
    status: "planning",
    createdAt: now,
    updatedAt: now
  };
  const next = sortTrips([...readTripsAll(uid), trip]);
  writeTripsAll(uid, next);
  setSelectedTripId(uid, trip.id);
  emitTripsUpdated(uid);
  void queueTripForFainanceShareSync(uid, trip).catch(() => undefined);
  return trip;
}

export function updateTrip(uid: string, tripId: string, patch: Partial<Omit<TripSummary, "id" | "ownerUid" | "createdAt">>): TripSummary | null {
  const current = readTripsAll(uid);
  const existing = current.find((trip) => trip.id === tripId);
  if (!existing) return null;
  if (existing.archivedAt && Object.keys(patch).some((key) => key !== "archivedAt")) {
    notifyArchivedMutationBlocked(uid, tripId);
    return existing;
  }
  const updated: TripSummary = { ...existing, ...patch, id: existing.id, ownerUid: existing.ownerUid, createdAt: existing.createdAt, updatedAt: new Date().toISOString() };
  const next = sortTrips(current.map((trip) => trip.id === tripId ? updated : trip));
  writeTripsAll(uid, next);
  emitTripsUpdated(uid);
  scheduleTripVersionSnapshot(uid, tripId);
  void queueTripForFainanceShareSync(uid, updated).catch(() => undefined);
  return updated;
}

export function upsertTripSummary(uid: string, trip: TripSummary): TripSummary {
  let normalized = normalizeTrip(trip, uid) || { ...trip, ownerUid: trip.ownerUid || uid };
  const current = readTripsAll(uid);
  const existing = current.find((item) => item.id === normalized.id);
  if (existing?.deletedAt && normalized.deletedAt === undefined) {
    normalized = { ...normalized, deletedAt: existing.deletedAt };
  }
  const next = sortTrips([normalized, ...current.filter((item) => item.id !== normalized.id)]);
  writeTripsAll(uid, next);
  emitTripsUpdated(uid);
  return normalized;
}

export function removeTripLocal(uid: string, tripId: string): TripSummary[] {
  const next = readTripsAll(uid).filter((trip) => trip.id !== tripId);
  writeTripsAll(uid, next);
  try { localStorage.removeItem(tripVersionHistoryKey(uid, tripId)); } catch { /* best effort */ }
  if (getSelectedTripId(uid) === tripId) setSelectedTripId(uid, null);
  emitTripsUpdated(uid);
  return listTrips(uid);
}

export function moveTripToTrash(uid: string, tripId: string, deletedAt = new Date().toISOString()): TripSummary | null {
  const current = readTripsAll(uid);
  const existing = current.find((trip) => trip.id === tripId);
  if (!existing) return null;
  const updated: TripSummary = { ...existing, deletedAt, updatedAt: deletedAt };
  writeTripsAll(uid, current.map((trip) => trip.id === tripId ? updated : trip));
  if (getSelectedTripId(uid) === tripId) setSelectedTripId(uid, null);
  emitTripsUpdated(uid);
  return updated;
}

export function restoreTripFromTrash(uid: string, tripId: string): TripSummary | null {
  const current = readTripsAll(uid);
  const existing = current.find((trip) => trip.id === tripId);
  if (!existing) return null;
  const updated: TripSummary = { ...existing, deletedAt: null, updatedAt: new Date().toISOString() };
  writeTripsAll(uid, current.map((trip) => trip.id === tripId ? updated : trip));
  emitTripsUpdated(uid);
  return updated;
}

export function removeTripFromTrashLocal(uid: string, tripId: string): void {
  const next = readTripsAll(uid).filter((trip) => trip.id !== tripId);
  writeTripsAll(uid, next);
  try { localStorage.removeItem(tripVersionHistoryKey(uid, tripId)); } catch { /* best effort */ }
  emitTripsUpdated(uid);
}

export function tripTrashDaysLeft(trip: TripSummary, retentionDays = 30): number {
  const deletedAt = Date.parse(String(trip.deletedAt || ""));
  if (!Number.isFinite(deletedAt)) return retentionDays;
  const purgeAt = deletedAt + retentionDays * 86400000;
  return Math.max(0, Math.ceil((purgeAt - Date.now()) / 86400000));
}

export function getSelectedTripId(uid: string): string | null {
  try {
    return localStorage.getItem(selectedTripKey(uid));
  } catch {
    return null;
  }
}

export function setSelectedTripId(uid: string, tripId: string | null): void {
  try {
    if (tripId) localStorage.setItem(selectedTripKey(uid), tripId);
    else localStorage.removeItem(selectedTripKey(uid));
  } catch {
    // Selection is convenience state only; the UI can continue without persistence.
  }
}

export type UsefulInfoFieldId =
  | "plugType"
  | "currency"
  | "exchangeRate"
  | "drinkingWater"
  | "taxiApps"
  | "foodDeliveryApps"
  | "embassy"
  | "consulates"
  | "emergencyNumber"
  | "specialPrecautions"
  | "governmentType"
  | "religion"
  | "publicAffection"
  | "lgbtq"
  | "civilRights"
  | "requiredVaccines"
  | "healthInsurance"
  | "visa"
  | "passport"
  | "drivingSide"
  | "internationalDrivingPermit";

export type UsefulInfoSource = "manual" | "ai" | "database" | "mixed";

export interface TripUsefulInfo {
  values: Record<UsefulInfoFieldId, string>;
  descriptions?: Partial<Record<UsefulInfoFieldId, string>>;
  source: UsefulInfoSource;
  updatedAt: string;
  aiGeneratedAt?: string;
  countryFactsVersion?: string;
  /** Last approved global values applied to this trip, for per-field override protection. */
  approvedCountryValues?: Partial<Record<UsefulInfoFieldId, string>>;
  approvedCountryDescriptions?: Partial<Record<UsefulInfoFieldId, string>>;
  destinationCountryCode?: string;
  nationalityCode?: string;
  autoExchangeRateValue?: string;
  autoExchangeRateUpdatedAt?: string;
  autoEmergencyNumberValue?: string;
  autoEmergencyNumberUpdatedAt?: string;
  autoRequirementsValues?: Partial<Record<UsefulInfoFieldId, string>>;
  autoRequirementsUpdatedAt?: string;
  autoRequirementsDatasetVersion?: string;
}

export const usefulInfoFieldIds: UsefulInfoFieldId[] = [
  "plugType",
  "currency",
  "exchangeRate",
  "drinkingWater",
  "taxiApps",
  "foodDeliveryApps",
  "embassy",
  "consulates",
  "emergencyNumber",
  "specialPrecautions",
  "governmentType",
  "religion",
  "publicAffection",
  "lgbtq",
  "civilRights",
  "requiredVaccines",
  "healthInsurance",
  "visa",
  "passport",
  "drivingSide",
  "internationalDrivingPermit"
];

function usefulInfoKey(uid: string, tripId: string): string {
  return `tripai.tripUsefulInfo.v1.${uid}.${tripId}`;
}

function emptyUsefulInfoValues(): Record<UsefulInfoFieldId, string> {
  return usefulInfoFieldIds.reduce((result, field) => {
    result[field] = "";
    return result;
  }, {} as Record<UsefulInfoFieldId, string>);
}

export function createEmptyUsefulInfo(): TripUsefulInfo {
  return {
    values: emptyUsefulInfoValues(),
    source: "manual",
    updatedAt: ""
  };
}

export function loadTripUsefulInfo(uid: string, tripId: string): TripUsefulInfo {
  try {
    const raw = localStorage.getItem(usefulInfoKey(uid, tripId));
    if (!raw) return createEmptyUsefulInfo();
    const parsed = JSON.parse(raw) as Partial<TripUsefulInfo>;
    const values = emptyUsefulInfoValues();
    if (parsed.values && typeof parsed.values === "object") {
      for (const field of usefulInfoFieldIds) {
        const value = (parsed.values as Partial<Record<UsefulInfoFieldId, unknown>>)[field];
        values[field] = essentialUsefulInfoText(value);
      }
    }
    return {
      values,
      descriptions: parsed.descriptions && typeof parsed.descriptions === "object" && !Array.isArray(parsed.descriptions)
        ? Object.fromEntries(usefulInfoFieldIds.filter((field) => typeof parsed.descriptions?.[field] === "string").map((field) => [field, essentialUsefulInfoText(parsed.descriptions![field])])) as Partial<Record<UsefulInfoFieldId, string>>
        : undefined,
      source: parsed.source === "ai" || parsed.source === "database" || parsed.source === "mixed" ? parsed.source : "manual",
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : "",
      aiGeneratedAt: typeof parsed.aiGeneratedAt === "string" ? parsed.aiGeneratedAt : undefined,
      countryFactsVersion: typeof parsed.countryFactsVersion === "string" ? parsed.countryFactsVersion : undefined,
      approvedCountryValues: parsed.approvedCountryValues && typeof parsed.approvedCountryValues === "object"
        ? Object.fromEntries(usefulInfoFieldIds
            .filter((field) => typeof parsed.approvedCountryValues?.[field] === "string")
            .map((field) => [field, essentialUsefulInfoText(parsed.approvedCountryValues![field])])) as Partial<Record<UsefulInfoFieldId, string>>
        : undefined,
      approvedCountryDescriptions: parsed.approvedCountryDescriptions && typeof parsed.approvedCountryDescriptions === "object" && !Array.isArray(parsed.approvedCountryDescriptions)
        ? Object.fromEntries(usefulInfoFieldIds.filter((field) => typeof parsed.approvedCountryDescriptions?.[field] === "string").map((field) => [field, essentialUsefulInfoText(parsed.approvedCountryDescriptions![field])])) as Partial<Record<UsefulInfoFieldId, string>>
        : undefined,
      destinationCountryCode: typeof parsed.destinationCountryCode === "string" ? parsed.destinationCountryCode : undefined,
      nationalityCode: typeof parsed.nationalityCode === "string" ? parsed.nationalityCode : undefined,
      autoExchangeRateValue: typeof parsed.autoExchangeRateValue === "string" ? parsed.autoExchangeRateValue : undefined,
      autoExchangeRateUpdatedAt: typeof parsed.autoExchangeRateUpdatedAt === "string" ? parsed.autoExchangeRateUpdatedAt : undefined,
      autoEmergencyNumberValue: typeof parsed.autoEmergencyNumberValue === "string" ? parsed.autoEmergencyNumberValue : undefined,
      autoEmergencyNumberUpdatedAt: typeof parsed.autoEmergencyNumberUpdatedAt === "string" ? parsed.autoEmergencyNumberUpdatedAt : undefined,
      autoRequirementsValues: parsed.autoRequirementsValues && typeof parsed.autoRequirementsValues === "object"
        ? Object.fromEntries(usefulInfoFieldIds.map((field) => [field, typeof (parsed.autoRequirementsValues as Partial<Record<UsefulInfoFieldId, unknown>>)[field] === "string" ? essentialUsefulInfoText((parsed.autoRequirementsValues as Partial<Record<UsefulInfoFieldId, unknown>>)[field]) : ""]).filter(([, value]) => Boolean(value))) as Partial<Record<UsefulInfoFieldId, string>>
        : undefined,
      autoRequirementsUpdatedAt: typeof parsed.autoRequirementsUpdatedAt === "string" ? parsed.autoRequirementsUpdatedAt : undefined,
      autoRequirementsDatasetVersion: typeof parsed.autoRequirementsDatasetVersion === "string" ? parsed.autoRequirementsDatasetVersion : undefined
    };
  } catch {
    return createEmptyUsefulInfo();
  }
}

export function saveTripUsefulInfo(uid: string, tripId: string, info: TripUsefulInfo): TripUsefulInfo {
  if (blockArchivedMutation(uid, tripId)) return loadTripUsefulInfo(uid, tripId);
  const normalized: TripUsefulInfo = {
    values: usefulInfoFieldIds.reduce((result, field) => {
      result[field] = essentialUsefulInfoText(info.values[field]);
      return result;
    }, {} as Record<UsefulInfoFieldId, string>),
    descriptions: info.descriptions ? Object.fromEntries(Object.entries(info.descriptions).map(([key, value]) => [key, essentialUsefulInfoText(value)])) : undefined,
    source: info.source,
    updatedAt: info.updatedAt || new Date().toISOString(),
    aiGeneratedAt: info.aiGeneratedAt,
    countryFactsVersion: info.countryFactsVersion,
    approvedCountryValues: info.approvedCountryValues ? Object.fromEntries(Object.entries(info.approvedCountryValues).map(([key, value]) => [key, essentialUsefulInfoText(value)])) : undefined,
    approvedCountryDescriptions: info.approvedCountryDescriptions ? Object.fromEntries(Object.entries(info.approvedCountryDescriptions).map(([key, value]) => [key, essentialUsefulInfoText(value)])) : undefined,
    destinationCountryCode: info.destinationCountryCode,
    nationalityCode: info.nationalityCode,
    autoExchangeRateValue: info.autoExchangeRateValue,
    autoExchangeRateUpdatedAt: info.autoExchangeRateUpdatedAt,
    autoEmergencyNumberValue: info.autoEmergencyNumberValue,
    autoEmergencyNumberUpdatedAt: info.autoEmergencyNumberUpdatedAt,
    autoRequirementsValues: info.autoRequirementsValues ? Object.fromEntries(Object.entries(info.autoRequirementsValues).map(([key, value]) => [key, essentialUsefulInfoText(value)])) : undefined,
    autoRequirementsUpdatedAt: info.autoRequirementsUpdatedAt,
    autoRequirementsDatasetVersion: info.autoRequirementsDatasetVersion
  };
  localStorage.setItem(usefulInfoKey(uid, tripId), JSON.stringify(normalized));
  scheduleTripVersionSnapshot(uid, tripId);
  return normalized;
}

export interface BoardingPassItem {
  id: string;
  airline: string;
  flightNumber: string;
  passengerName: string;
  originCode: string;
  originCity: string;
  destinationCode: string;
  destinationCity: string;
  departureDate: string;
  departureTime: string;
  arrivalTime: string;
  terminal: string;
  gate: string;
  seat: string;
  boardingTime: string;
  bookingReference: string;
  createdAt: string;
  updatedAt: string;
}

export type BoardingPassDraft = Omit<BoardingPassItem, "id" | "createdAt" | "updatedAt">;

function boardingPassKey(uid: string, tripId: string): string {
  return `tripai.boardingPass.v1.${uid}.${tripId}`;
}

function normalizeBoardingPass(value: unknown): BoardingPassItem | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<BoardingPassItem>;
  if (!candidate.id) return null;
  return {
    id: String(candidate.id),
    airline: String(candidate.airline || ""),
    flightNumber: String(candidate.flightNumber || ""),
    passengerName: String(candidate.passengerName || ""),
    originCode: String(candidate.originCode || "").toUpperCase().slice(0, 3),
    originCity: String(candidate.originCity || ""),
    destinationCode: String(candidate.destinationCode || "").toUpperCase().slice(0, 3),
    destinationCity: String(candidate.destinationCity || ""),
    departureDate: String(candidate.departureDate || ""),
    departureTime: String(candidate.departureTime || ""),
    arrivalTime: String(candidate.arrivalTime || ""),
    terminal: String(candidate.terminal || ""),
    gate: String(candidate.gate || ""),
    seat: String(candidate.seat || ""),
    boardingTime: String(candidate.boardingTime || ""),
    bookingReference: String(candidate.bookingReference || ""),
    createdAt: String(candidate.createdAt || new Date().toISOString()),
    updatedAt: String(candidate.updatedAt || candidate.createdAt || new Date().toISOString())
  };
}

export function listBoardingPasses(uid: string, tripId: string): BoardingPassItem[] {
  try {
    const raw = localStorage.getItem(boardingPassKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizeBoardingPass)
      .filter((item): item is BoardingPassItem => Boolean(item))
      .sort((a, b) => `${a.departureDate}T${a.departureTime}`.localeCompare(`${b.departureDate}T${b.departureTime}`));
  } catch {
    return [];
  }
}

function persistBoardingPasses(uid: string, tripId: string, items: BoardingPassItem[]): BoardingPassItem[] {
  if (blockArchivedMutation(uid, tripId)) return listBoardingPasses(uid, tripId);
  localStorage.setItem(boardingPassKey(uid, tripId), JSON.stringify(items));
  scheduleTripVersionSnapshot(uid, tripId);
  return items;
}

export function saveBoardingPass(uid: string, tripId: string, draft: BoardingPassDraft, id?: string): BoardingPassItem {
  const now = new Date().toISOString();
  const current = listBoardingPasses(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : undefined;
  const item: BoardingPassItem = {
    id: existing?.id || id || (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `bp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`),
    airline: String(draft.airline || "").trim(),
    flightNumber: String(draft.flightNumber || "").trim().toUpperCase(),
    passengerName: String(draft.passengerName || "").trim(),
    originCode: String(draft.originCode || "").trim().toUpperCase().slice(0, 3),
    originCity: String(draft.originCity || "").trim(),
    destinationCode: String(draft.destinationCode || "").trim().toUpperCase().slice(0, 3),
    destinationCity: String(draft.destinationCity || "").trim(),
    departureDate: String(draft.departureDate || ""),
    departureTime: String(draft.departureTime || ""),
    arrivalTime: String(draft.arrivalTime || ""),
    terminal: String(draft.terminal || "").trim(),
    gate: String(draft.gate || "").trim(),
    seat: String(draft.seat || "").trim().toUpperCase(),
    boardingTime: String(draft.boardingTime || ""),
    bookingReference: String(draft.bookingReference || "").trim().toUpperCase(),
    createdAt: existing?.createdAt || now,
    updatedAt: now
  };
  const next = existing ? current.map((entry) => entry.id === item.id ? item : entry) : [...current, item];
  persistBoardingPasses(uid, tripId, next);
  return item;
}

export function deleteBoardingPass(uid: string, tripId: string, id: string): BoardingPassItem[] {
  return persistBoardingPasses(uid, tripId, listBoardingPasses(uid, tripId).filter((item) => item.id !== id));
}

export interface TripTicketItem {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  queueId: string;
  storagePath: string;
  url?: string;
  status: "pending" | "uploaded";
  createdAt: string;
  updatedAt: string;
}

function tripTicketsKey(uid: string, tripId: string): string {
  return `tripai.tripTickets.v1.${uid}.${tripId}`;
}

function normalizeTripTicket(value: unknown): TripTicketItem | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<TripTicketItem>;
  if (!candidate.id || !candidate.name || !candidate.queueId) return null;
  return {
    id: String(candidate.id),
    name: String(candidate.name),
    mimeType: String(candidate.mimeType || "application/octet-stream"),
    size: Number(candidate.size || 0),
    queueId: String(candidate.queueId),
    storagePath: String(candidate.storagePath || ""),
    url: typeof candidate.url === "string" && candidate.url ? candidate.url : undefined,
    status: candidate.status === "uploaded" && candidate.url ? "uploaded" : "pending",
    createdAt: String(candidate.createdAt || new Date().toISOString()),
    updatedAt: String(candidate.updatedAt || candidate.createdAt || new Date().toISOString())
  };
}

export function listTripTickets(uid: string, tripId: string): TripTicketItem[] {
  try {
    const raw = localStorage.getItem(tripTicketsKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizeTripTicket)
      .filter((item): item is TripTicketItem => Boolean(item))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch {
    return [];
  }
}

function persistTripTickets(uid: string, tripId: string, items: TripTicketItem[]): TripTicketItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripTickets(uid, tripId);
  localStorage.setItem(tripTicketsKey(uid, tripId), JSON.stringify(items));
  scheduleTripVersionSnapshot(uid, tripId);
  return items;
}

export function saveTripTicket(uid: string, tripId: string, input: Omit<TripTicketItem, "createdAt" | "updatedAt" | "status" | "url"> & { url?: string }): TripTicketItem {
  const now = new Date().toISOString();
  const current = listTripTickets(uid, tripId);
  const item: TripTicketItem = {
    ...input,
    status: input.url ? "uploaded" : "pending",
    url: input.url,
    createdAt: now,
    updatedAt: now
  };
  persistTripTickets(uid, tripId, [item, ...current.filter((entry) => entry.id !== item.id)]);
  return item;
}

export function markTripTicketUploaded(uid: string, tripId: string, queueId: string, url: string): TripTicketItem[] {
  const now = new Date().toISOString();
  const next = listTripTickets(uid, tripId).map((item) => item.queueId === queueId
    ? { ...item, url, status: "uploaded" as const, updatedAt: now }
    : item);
  return persistTripTickets(uid, tripId, next);
}


export function renameTripTicket(uid: string, tripId: string, ticketId: string, displayName: string): TripTicketItem[] {
  const nextName = String(displayName || "").trim();
  if (!nextName) return listTripTickets(uid, tripId);
  const now = new Date().toISOString();
  const next = listTripTickets(uid, tripId).map((item) => item.id === ticketId
    ? { ...item, name: nextName, updatedAt: now }
    : item);
  return persistTripTickets(uid, tripId, next);
}

export function deleteTripTicket(uid: string, tripId: string, ticketId: string): TripTicketItem[] {
  return persistTripTickets(uid, tripId, listTripTickets(uid, tripId).filter((item) => item.id !== ticketId));
}

export type TripDocumentSystemFolderKey = "passport" | "id" | "drivingLicense" | "healthCard" | "visas" | "other";

export interface TripDocumentFolder {
  id: string;
  name: string;
  systemKey?: TripDocumentSystemFolderKey;
  createdAt: string;
}

export interface TripDocumentItem {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  queueId: string;
  storagePath: string;
  url?: string;
  status: "pending" | "uploaded";
  folderId: string;
  participantName: string;
  createdAt: string;
  updatedAt: string;
}

const systemDocumentFolders: Array<{ id: string; name: string; systemKey: TripDocumentSystemFolderKey }> = [
  { id: "passport", name: "Passport", systemKey: "passport" },
  { id: "id", name: "ID", systemKey: "id" },
  { id: "drivingLicense", name: "Driving license", systemKey: "drivingLicense" },
  { id: "healthCard", name: "Health card", systemKey: "healthCard" },
  { id: "visas", name: "Visas", systemKey: "visas" },
  { id: "other", name: "Other", systemKey: "other" }
];

function tripDocumentFoldersKey(uid: string, tripId: string): string {
  return `tripai.tripDocumentFolders.v1.${uid}.${tripId}`;
}

function tripDocumentsKey(uid: string, tripId: string): string {
  return `tripai.tripDocuments.v1.${uid}.${tripId}`;
}

export function listTripDocumentFolders(uid: string, tripId: string): TripDocumentFolder[] {
  const now = new Date().toISOString();
  // System folders exist with the trip. A new timestamp on every read causes
  // perpetual cloud writes and invalidates otherwise unchanged backups.
  const systemCreatedAt = listTrips(uid).find(trip => trip.id === tripId)?.createdAt || "1970-01-01T00:00:00.000Z";
  const system = systemDocumentFolders.map((folder) => ({ ...folder, createdAt: systemCreatedAt }));
  try {
    const raw = localStorage.getItem(tripDocumentFoldersKey(uid, tripId));
    if (!raw) return system;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return system;
    const custom = parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const candidate = value as Partial<TripDocumentFolder>;
      if (!candidate.id || !candidate.name || candidate.systemKey) return [];
      return [{ id: String(candidate.id), name: String(candidate.name), createdAt: String(candidate.createdAt || now) } as TripDocumentFolder];
    });
    return [...system, ...custom];
  } catch {
    return system;
  }
}

function persistCustomDocumentFolders(uid: string, tripId: string, folders: TripDocumentFolder[]): TripDocumentFolder[] {
  if (blockArchivedMutation(uid, tripId)) return listTripDocumentFolders(uid, tripId);
  const custom = folders.filter((folder) => !folder.systemKey);
  localStorage.setItem(tripDocumentFoldersKey(uid, tripId), JSON.stringify(custom));
  return listTripDocumentFolders(uid, tripId);
}

export function createTripDocumentFolder(uid: string, tripId: string, name: string): TripDocumentFolder[] {
  const current = listTripDocumentFolders(uid, tripId);
  const normalized = name.trim();
  if (!normalized) return current;
  if (current.some((folder) => folder.name.toLocaleLowerCase() === normalized.toLocaleLowerCase())) return current;
  const id = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `folder_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  return persistCustomDocumentFolders(uid, tripId, [...current, { id, name: normalized, createdAt: new Date().toISOString() }]);
}

function normalizeTripDocument(value: unknown): TripDocumentItem | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<TripDocumentItem>;
  if (!candidate.id || !candidate.name || !candidate.queueId) return null;
  return {
    id: String(candidate.id),
    name: String(candidate.name),
    mimeType: String(candidate.mimeType || "application/octet-stream"),
    size: Number(candidate.size || 0),
    queueId: String(candidate.queueId),
    storagePath: String(candidate.storagePath || ""),
    url: typeof candidate.url === "string" && candidate.url ? candidate.url : undefined,
    status: candidate.status === "uploaded" && candidate.url ? "uploaded" : "pending",
    folderId: String(candidate.folderId || "other"),
    participantName: String(candidate.participantName || ""),
    createdAt: String(candidate.createdAt || new Date().toISOString()),
    updatedAt: String(candidate.updatedAt || candidate.createdAt || new Date().toISOString())
  };
}

export function listTripDocuments(uid: string, tripId: string): TripDocumentItem[] {
  try {
    const raw = localStorage.getItem(tripDocumentsKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeTripDocument).filter((item): item is TripDocumentItem => Boolean(item)).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch {
    return [];
  }
}

function persistTripDocuments(uid: string, tripId: string, items: TripDocumentItem[]): TripDocumentItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripDocuments(uid, tripId);
  localStorage.setItem(tripDocumentsKey(uid, tripId), JSON.stringify(items));
  scheduleTripVersionSnapshot(uid, tripId);
  return items;
}

export function saveTripDocument(uid: string, tripId: string, input: Omit<TripDocumentItem, "createdAt" | "updatedAt" | "status" | "url"> & { url?: string }): TripDocumentItem {
  const now = new Date().toISOString();
  const item: TripDocumentItem = { ...input, status: input.url ? "uploaded" : "pending", url: input.url, createdAt: now, updatedAt: now };
  persistTripDocuments(uid, tripId, [item, ...listTripDocuments(uid, tripId).filter((entry) => entry.id !== item.id)]);
  return item;
}

export function markTripDocumentUploaded(uid: string, tripId: string, queueId: string, url: string): TripDocumentItem[] {
  const now = new Date().toISOString();
  const next = listTripDocuments(uid, tripId).map((item) => item.queueId === queueId ? { ...item, url, status: "uploaded" as const, updatedAt: now } : item);
  return persistTripDocuments(uid, tripId, next);
}

export function renameTripDocument(uid: string, tripId: string, documentId: string, name: string): TripDocumentItem[] {
  const normalized = name.trim();
  if (!normalized) return listTripDocuments(uid, tripId);
  const now = new Date().toISOString();
  return persistTripDocuments(uid, tripId, listTripDocuments(uid, tripId).map((item) => item.id === documentId ? { ...item, name: normalized, updatedAt: now } : item));
}

export function updateTripDocumentMeta(uid: string, tripId: string, documentId: string, patch: Pick<TripDocumentItem, "folderId" | "participantName">): TripDocumentItem[] {
  const now = new Date().toISOString();
  return persistTripDocuments(uid, tripId, listTripDocuments(uid, tripId).map((item) => item.id === documentId ? { ...item, ...patch, updatedAt: now } : item));
}

export function deleteTripDocument(uid: string, tripId: string, documentId: string): TripDocumentItem[] {
  return persistTripDocuments(uid, tripId, listTripDocuments(uid, tripId).filter((item) => item.id !== documentId));
}

export interface TripHotelItem {
  id: string;
  name: string;
  city: string;
  address: string;
  checkIn: string;
  checkOut: string;
  bookingReference: string;
  bookedWith: string;
  bookedWithOther: string;
  notes: string;
  latitude?: number;
  longitude?: number;
  googlePlaceId?: string;
  googleRating?: number;
  googleRatingCount?: number;
  createdAt: string;
  updatedAt: string;
}

function tripHotelsKey(uid: string, tripId: string): string {
  return `tripai.tripHotels.v1.${uid}.${tripId}`;
}

export function listTripHotels(uid: string, tripId: string): TripHotelItem[] {
  try {
    const raw = localStorage.getItem(tripHotelsKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const item = value as Partial<TripHotelItem>;
      if (!item.id || !item.name) return [];
      return [{
        id: String(item.id), name: String(item.name), city: String(item.city || ""), address: String(item.address || ""),
        checkIn: String(item.checkIn || ""), checkOut: String(item.checkOut || ""), bookingReference: String(item.bookingReference || ""),
        bookedWith: String(item.bookedWith || ""), bookedWithOther: String(item.bookedWithOther || ""), notes: String(item.notes || ""),
        latitude: typeof item.latitude === "number" ? item.latitude : undefined, longitude: typeof item.longitude === "number" ? item.longitude : undefined,
        googlePlaceId: String(item.googlePlaceId || "") || undefined,
        googleRating: typeof item.googleRating === "number" ? item.googleRating : undefined,
        googleRatingCount: typeof item.googleRatingCount === "number" ? item.googleRatingCount : undefined,
        createdAt: String(item.createdAt || new Date().toISOString()), updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString())
      } as TripHotelItem];
    }).sort((a, b) => {
      if (a.checkIn && b.checkIn && a.checkIn !== b.checkIn) return a.checkIn.localeCompare(b.checkIn);
      if (a.checkIn && !b.checkIn) return -1;
      if (!a.checkIn && b.checkIn) return 1;
      if (a.checkOut && b.checkOut && a.checkOut !== b.checkOut) return a.checkOut.localeCompare(b.checkOut);
      return a.name.localeCompare(b.name);
    });
  } catch { return []; }
}

function sortTripHotels(items: TripHotelItem[]): TripHotelItem[] {
  return [...items].sort((a, b) => {
    if (a.checkIn && b.checkIn && a.checkIn !== b.checkIn) return a.checkIn.localeCompare(b.checkIn);
    if (a.checkIn && !b.checkIn) return -1;
    if (!a.checkIn && b.checkIn) return 1;
    if (a.checkOut && b.checkOut && a.checkOut !== b.checkOut) return a.checkOut.localeCompare(b.checkOut);
    return a.name.localeCompare(b.name);
  });
}

function persistTripHotels(uid: string, tripId: string, items: TripHotelItem[]): TripHotelItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripHotels(uid, tripId);
  const sorted = sortTripHotels(items);
  localStorage.setItem(tripHotelsKey(uid, tripId), JSON.stringify(sorted));
  scheduleTripVersionSnapshot(uid, tripId);
  return sorted;
}

export function saveTripHotel(uid: string, tripId: string, draft: Omit<TripHotelItem, "id" | "createdAt" | "updatedAt">, id?: string): TripHotelItem[] {
  const now = new Date().toISOString();
  const current = listTripHotels(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : undefined;
  const item: TripHotelItem = { ...draft, id: existing?.id || (crypto.randomUUID ? crypto.randomUUID() : `hotel_${Date.now()}`), createdAt: existing?.createdAt || now, updatedAt: now };
  return persistTripHotels(uid, tripId, [item, ...current.filter((entry) => entry.id !== item.id)]);
}

export function deleteTripHotel(uid: string, tripId: string, id: string): TripHotelItem[] {
  return persistTripHotels(uid, tripId, listTripHotels(uid, tripId).filter((item) => item.id !== id));
}


export interface TripRestaurantItem {
  id: string;
  name: string;
  city: string;
  address: string;
  date: string;
  time: string;
  reservationWith: string;
  reservationWithOther: string;
  bookingReference: string;
  notes: string;
  latitude?: number;
  longitude?: number;
  googlePlaceId?: string;
  googleRating?: number;
  googleRatingCount?: number;
  createdAt: string;
  updatedAt: string;
}

function tripRestaurantsKey(uid: string, tripId: string): string {
  return `tripai.tripRestaurants.v1.${uid}.${tripId}`;
}

export function listTripRestaurants(uid: string, tripId: string): TripRestaurantItem[] {
  try {
    const raw = localStorage.getItem(tripRestaurantsKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const item = value as Partial<TripRestaurantItem>;
      if (!item.id || !item.name) return [];
      return [{
        id: String(item.id), name: String(item.name), city: String(item.city || ""), address: String(item.address || ""),
        date: String(item.date || ""), time: String(item.time || ""), reservationWith: String(item.reservationWith || ""),
        reservationWithOther: String(item.reservationWithOther || ""), bookingReference: String(item.bookingReference || ""), notes: String(item.notes || ""),
        latitude: typeof item.latitude === "number" ? item.latitude : undefined, longitude: typeof item.longitude === "number" ? item.longitude : undefined,
        googlePlaceId: String(item.googlePlaceId || "") || undefined,
        googleRating: typeof item.googleRating === "number" ? item.googleRating : undefined,
        googleRatingCount: typeof item.googleRatingCount === "number" ? item.googleRatingCount : undefined,
        createdAt: String(item.createdAt || new Date().toISOString()), updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString())
      } as TripRestaurantItem];
    }).sort((a, b) => {
      if (a.date && b.date && a.date !== b.date) return a.date.localeCompare(b.date);
      if (a.date && !b.date) return -1;
      if (!a.date && b.date) return 1;
      if (a.time && b.time && a.time !== b.time) return a.time.localeCompare(b.time);
      return a.name.localeCompare(b.name);
    });
  } catch { return []; }
}

function sortTripRestaurants(items: TripRestaurantItem[]): TripRestaurantItem[] {
  return [...items].sort((a, b) => {
    if (a.date && b.date && a.date !== b.date) return a.date.localeCompare(b.date);
    if (a.date && !b.date) return -1;
    if (!a.date && b.date) return 1;
    if (a.time && b.time && a.time !== b.time) return a.time.localeCompare(b.time);
    return a.name.localeCompare(b.name);
  });
}

function persistTripRestaurants(uid: string, tripId: string, items: TripRestaurantItem[]): TripRestaurantItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripRestaurants(uid, tripId);
  const sorted = sortTripRestaurants(items);
  localStorage.setItem(tripRestaurantsKey(uid, tripId), JSON.stringify(sorted));
  scheduleTripVersionSnapshot(uid, tripId);
  return sorted;
}

export function saveTripRestaurant(uid: string, tripId: string, draft: Omit<TripRestaurantItem, "id" | "createdAt" | "updatedAt">, id?: string): TripRestaurantItem[] {
  const now = new Date().toISOString();
  const current = listTripRestaurants(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : undefined;
  const item: TripRestaurantItem = { ...draft, id: existing?.id || (crypto.randomUUID ? crypto.randomUUID() : `restaurant_${Date.now()}`), createdAt: existing?.createdAt || now, updatedAt: now };
  return persistTripRestaurants(uid, tripId, [item, ...current.filter((entry) => entry.id !== item.id)]);
}

export function deleteTripRestaurant(uid: string, tripId: string, id: string): TripRestaurantItem[] {
  return persistTripRestaurants(uid, tripId, listTripRestaurants(uid, tripId).filter((item) => item.id !== id));
}


export interface TripPlaceItem {
  id: string;
  name: string;
  city: string;
  address: string;
  startDate: string;
  endDate: string;
  notes: string;
  latitude?: number;
  longitude?: number;
  googlePlaceId?: string;
  photoIndex: number;
  customPhotoDataUrl?: string;
  customPhotoName?: string;
  createdAt: string;
  updatedAt: string;
}

function tripPlacesKey(uid: string, tripId: string): string {
  return `tripai.tripPlaces.v1.${uid}.${tripId}`;
}

export function listTripPlaces(uid: string, tripId: string): TripPlaceItem[] {
  try {
    const raw = localStorage.getItem(tripPlacesKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const item = value as Partial<TripPlaceItem>;
      if (!item.id || !item.name) return [];
      return [{
        id: String(item.id),
        name: String(item.name),
        city: String(item.city || ""),
        address: String(item.address || ""),
        startDate: String(item.startDate || ""),
        endDate: String(item.endDate || ""),
        notes: String(item.notes || ""),
        latitude: typeof item.latitude === "number" ? item.latitude : undefined,
        longitude: typeof item.longitude === "number" ? item.longitude : undefined,
        googlePlaceId: String(item.googlePlaceId || "") || undefined,
        photoIndex: Number.isFinite(Number(item.photoIndex)) ? Math.max(0, Number(item.photoIndex)) : 0,
        customPhotoDataUrl: String(item.customPhotoDataUrl || "") || undefined,
        customPhotoName: String(item.customPhotoName || "") || undefined,
        createdAt: String(item.createdAt || new Date().toISOString()),
        updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString())
      } as TripPlaceItem];
    }).sort((a, b) => {
      if (a.startDate && b.startDate && a.startDate !== b.startDate) return a.startDate.localeCompare(b.startDate);
      if (a.startDate && !b.startDate) return -1;
      if (!a.startDate && b.startDate) return 1;
      if (a.endDate && b.endDate && a.endDate !== b.endDate) return a.endDate.localeCompare(b.endDate);
      return a.name.localeCompare(b.name);
    });
  } catch {
    return [];
  }
}

function sortTripPlaces(items: TripPlaceItem[]): TripPlaceItem[] {
  return [...items].sort((a, b) => {
    if (a.startDate && b.startDate && a.startDate !== b.startDate) return a.startDate.localeCompare(b.startDate);
    if (a.startDate && !b.startDate) return -1;
    if (!a.startDate && b.startDate) return 1;
    if (a.endDate && b.endDate && a.endDate !== b.endDate) return a.endDate.localeCompare(b.endDate);
    return a.name.localeCompare(b.name);
  });
}

function persistTripPlaces(uid: string, tripId: string, items: TripPlaceItem[]): TripPlaceItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripPlaces(uid, tripId);
  const sorted = sortTripPlaces(items);
  localStorage.setItem(tripPlacesKey(uid, tripId), JSON.stringify(sorted));
  scheduleTripVersionSnapshot(uid, tripId);
  return sorted;
}

export function saveTripPlace(uid: string, tripId: string, draft: Omit<TripPlaceItem, "id" | "createdAt" | "updatedAt">, id?: string): TripPlaceItem[] {
  const now = new Date().toISOString();
  const current = listTripPlaces(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : undefined;
  const item: TripPlaceItem = {
    ...draft,
    id: existing?.id || (crypto.randomUUID ? crypto.randomUUID() : `place_${Date.now()}`),
    createdAt: existing?.createdAt || now,
    updatedAt: now
  };
  return persistTripPlaces(uid, tripId, [item, ...current.filter((entry) => entry.id !== item.id)]);
}

export function deleteTripPlace(uid: string, tripId: string, id: string): TripPlaceItem[] {
  return persistTripPlaces(uid, tripId, listTripPlaces(uid, tripId).filter((item) => item.id !== id));
}

export interface TripPointOfInterestItem {
  id: string;
  cityId: string;
  name: string;
  city: string;
  address: string;
  notes: string;
  visitDate: string;
  latitude?: number;
  longitude?: number;
  googlePlaceId?: string;
  research: string;
  researchUpdatedAt?: string;
  createdAt: string;
  updatedAt: string;
}

function tripPointsOfInterestKey(uid: string, tripId: string): string {
  return `tripai.tripPointsOfInterest.v1.${uid}.${tripId}`;
}

export function listTripPointsOfInterest(uid: string, tripId: string): TripPointOfInterestItem[] {
  try {
    const raw = localStorage.getItem(tripPointsOfInterestKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const item = value as Partial<TripPointOfInterestItem>;
      if (!item.id || !item.name) return [];
      return [{
        id: String(item.id),
        cityId: String(item.cityId || ""),
        name: String(item.name),
        city: String(item.city || ""),
        address: String(item.address || ""),
        notes: String(item.notes || ""),
        visitDate: String(item.visitDate || ""),
        latitude: typeof item.latitude === "number" ? item.latitude : undefined,
        longitude: typeof item.longitude === "number" ? item.longitude : undefined,
        googlePlaceId: String(item.googlePlaceId || "") || undefined,
        research: String(item.research || ""),
        researchUpdatedAt: String(item.researchUpdatedAt || "") || undefined,
        createdAt: String(item.createdAt || new Date().toISOString()),
        updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString())
      } as TripPointOfInterestItem];
    }).sort((a, b) => {
      if (a.visitDate && b.visitDate && a.visitDate !== b.visitDate) return a.visitDate.localeCompare(b.visitDate);
      if (a.visitDate && !b.visitDate) return -1;
      if (!a.visitDate && b.visitDate) return 1;
      return a.name.localeCompare(b.name);
    });
  } catch {
    return [];
  }
}

function sortTripPointsOfInterest(items: TripPointOfInterestItem[]): TripPointOfInterestItem[] {
  return [...items].sort((a, b) => {
    if (a.visitDate && b.visitDate && a.visitDate !== b.visitDate) return a.visitDate.localeCompare(b.visitDate);
    if (a.visitDate && !b.visitDate) return -1;
    if (!a.visitDate && b.visitDate) return 1;
    return a.name.localeCompare(b.name);
  });
}

function persistTripPointsOfInterest(uid: string, tripId: string, items: TripPointOfInterestItem[]): TripPointOfInterestItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripPointsOfInterest(uid, tripId);
  const sorted = sortTripPointsOfInterest(items);
  localStorage.setItem(tripPointsOfInterestKey(uid, tripId), JSON.stringify(sorted));
  scheduleTripVersionSnapshot(uid, tripId);
  return sorted;
}

export function saveTripPointOfInterest(
  uid: string,
  tripId: string,
  draft: Omit<TripPointOfInterestItem, "id" | "createdAt" | "updatedAt">,
  id?: string
): TripPointOfInterestItem[] {
  const now = new Date().toISOString();
  const current = listTripPointsOfInterest(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : undefined;
  const item: TripPointOfInterestItem = {
    ...draft,
    id: existing?.id || (crypto.randomUUID ? crypto.randomUUID() : `poi_${Date.now()}`),
    createdAt: existing?.createdAt || now,
    updatedAt: now
  };
  return persistTripPointsOfInterest(uid, tripId, [item, ...current.filter((entry) => entry.id !== item.id)]);
}

export function deleteTripPointOfInterest(uid: string, tripId: string, id: string): TripPointOfInterestItem[] {
  return persistTripPointsOfInterest(
    uid,
    tripId,
    listTripPointsOfInterest(uid, tripId).filter((item) => item.id !== id)
  );
}


export interface TripChecklistList {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface TripChecklistItem {
  id: string;
  checklistId: string;
  title: string;
  category: string;
  notes: string;
  date: string;
  completed: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Independent system-checklist confirmations (not user checklist items). */
export type SmartPreparationDecision = "confirmed" | "notApplicable";
export type SmartPreparationStatuses = Record<string, SmartPreparationDecision>;

function smartPreparationKey(uid: string, tripId: string): string {
  return `mundely.tripSmartPreparation.v1.${uid}.${tripId}`;
}

export function listSmartPreparationStatuses(uid: string, tripId: string): SmartPreparationStatuses {
  try {
    const raw = localStorage.getItem(smartPreparationKey(uid, tripId));
    const value: unknown = raw ? JSON.parse(raw) : {};
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return Object.fromEntries(Object.entries(value).filter(([key, status]) =>
      /^[a-zA-Z]{3,32}$/.test(key) && (status === "confirmed" || status === "notApplicable")
    )) as SmartPreparationStatuses;
  } catch { return {}; }
}

export function setSmartPreparationStatus(
  uid: string, tripId: string, taskId: string, status: SmartPreparationDecision | null
): SmartPreparationStatuses {
  if (blockArchivedMutation(uid, tripId)) return listSmartPreparationStatuses(uid, tripId);
  const next = { ...listSmartPreparationStatuses(uid, tripId) };
  if (status === null) delete next[taskId];
  else next[taskId] = status;
  localStorage.setItem(smartPreparationKey(uid, tripId), JSON.stringify(next));
  scheduleTripVersionSnapshot(uid, tripId);
  return next;
}

/** Guided organization is independent from every user-defined checklist. */
function guidedPlanKey(uid: string, tripId: string): string {
  return `mundely.tripGuidedPlan.v1.${uid}.${tripId}`;
}

export function listGuidedPlan(uid: string, tripId: string): GuidedPlan | null {
  try {
    const raw = localStorage.getItem(guidedPlanKey(uid, tripId));
    return raw === null ? null : normalizeGuidedPlan(JSON.parse(raw));
  } catch { return null; }
}

export function saveGuidedPlan(uid: string, tripId: string, steps: GuidedPlan): GuidedPlan {
  if (blockArchivedMutation(uid, tripId)) return listGuidedPlan(uid, tripId) || [];
  const validated = normalizeGuidedPlan(steps);
  if (!validated || validated.length !== steps.length) throw new Error("Invalid guided plan");
  localStorage.setItem(guidedPlanKey(uid, tripId), JSON.stringify(validated));
  scheduleTripVersionSnapshot(uid, tripId);
  return validated;
}

function tripSetupKey(uid: string, tripId: string): string {
  return `mundely.tripSetup.v1.${uid}.${tripId}`;
}

export function listTripSetup(uid: string, tripId: string): TripSetup | null {
  try {
    const raw = localStorage.getItem(tripSetupKey(uid, tripId));
    return raw === null ? null : normalizeTripSetup(JSON.parse(raw));
  } catch { return null; }
}

export function saveTripSetup(uid: string, tripId: string, setup: TripSetup): TripSetup {
  if (blockArchivedMutation(uid, tripId)) return listTripSetup(uid, tripId) || setup;
  const verified = normalizeTripSetup(setup);
  if (!verified) throw new Error("Invalid trip setup");
  localStorage.setItem(tripSetupKey(uid, tripId), JSON.stringify(verified));
  scheduleTripVersionSnapshot(uid, tripId);
  return verified;
}

const DEFAULT_CHECKLIST_ID = "default";

function tripChecklistListsKey(uid: string, tripId: string): string {
  return `tripai.tripChecklistLists.v1.${uid}.${tripId}`;
}

function tripChecklistKey(uid: string, tripId: string): string {
  return `tripai.tripChecklist.v1.${uid}.${tripId}`;
}

function defaultChecklistList(): TripChecklistList {
  const now = new Date().toISOString();
  return { id: DEFAULT_CHECKLIST_ID, name: "", createdAt: now, updatedAt: now };
}

export function listTripChecklistLists(uid: string, tripId: string): TripChecklistList[] {
  try {
    const raw = localStorage.getItem(tripChecklistListsKey(uid, tripId));
    if (!raw) return [defaultChecklistList()];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [defaultChecklistList()];
    const lists = parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const item = value as Partial<TripChecklistList>;
      if (!item.id) return [];
      return [{
        id: String(item.id),
        name: String(item.name || ""),
        createdAt: String(item.createdAt || new Date().toISOString()),
        updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString())
      } satisfies TripChecklistList];
    });
    if (!lists.some((item) => item.id === DEFAULT_CHECKLIST_ID)) lists.unshift(defaultChecklistList());
    return lists;
  } catch {
    return [defaultChecklistList()];
  }
}

function persistTripChecklistLists(uid: string, tripId: string, lists: TripChecklistList[]): TripChecklistList[] {
  if (blockArchivedMutation(uid, tripId)) return listTripChecklistLists(uid, tripId);
  const normalized = lists.length > 0 ? lists : [defaultChecklistList()];
  localStorage.setItem(tripChecklistListsKey(uid, tripId), JSON.stringify(normalized));
  scheduleTripVersionSnapshot(uid, tripId);
  return normalized;
}

export function createTripChecklistList(uid: string, tripId: string, name: string): TripChecklistList[] {
  const trimmed = name.trim();
  if (!trimmed) return listTripChecklistLists(uid, tripId);
  const now = new Date().toISOString();
  const item: TripChecklistList = {
    id: crypto.randomUUID ? crypto.randomUUID() : `checklist_${Date.now()}`,
    name: trimmed,
    createdAt: now,
    updatedAt: now
  };
  return persistTripChecklistLists(uid, tripId, [...listTripChecklistLists(uid, tripId), item]);
}

export function renameTripChecklistList(uid: string, tripId: string, id: string, name: string): TripChecklistList[] {
  const trimmed = name.trim();
  if (!trimmed) return listTripChecklistLists(uid, tripId);
  const now = new Date().toISOString();
  return persistTripChecklistLists(uid, tripId, listTripChecklistLists(uid, tripId).map((item) => item.id === id ? { ...item, name: trimmed, updatedAt: now } : item));
}

export function deleteTripChecklistList(uid: string, tripId: string, id: string): { lists: TripChecklistList[]; items: TripChecklistItem[] } {
  if (id === DEFAULT_CHECKLIST_ID) return { lists: listTripChecklistLists(uid, tripId), items: listTripChecklist(uid, tripId) };
  const lists = persistTripChecklistLists(uid, tripId, listTripChecklistLists(uid, tripId).filter((item) => item.id !== id));
  const items = persistTripChecklist(uid, tripId, listTripChecklist(uid, tripId).filter((item) => item.checklistId !== id));
  return { lists, items };
}

function sortTripChecklistItems(items: TripChecklistItem[]): TripChecklistItem[] {
  return [...items].sort((a, b) => {
    const completedOrder = Number(a.completed) - Number(b.completed);
    if (completedOrder !== 0) return completedOrder;
    if (a.date && b.date && a.date !== b.date) return a.date.localeCompare(b.date);
    if (a.date && !b.date) return -1;
    if (!a.date && b.date) return 1;
    return a.title.localeCompare(b.title);
  });
}

export function listTripChecklist(uid: string, tripId: string): TripChecklistItem[] {
  try {
    const raw = localStorage.getItem(tripChecklistKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return sortTripChecklistItems(parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const item = value as Partial<TripChecklistItem>;
      if (!item.id || !item.title) return [];
      return [{
        id: String(item.id),
        checklistId: String(item.checklistId || DEFAULT_CHECKLIST_ID),
        title: String(item.title),
        category: String(item.category || "action"),
        notes: String(item.notes || ""),
        date: String(item.date || ""),
        completed: Boolean(item.completed),
        createdAt: String(item.createdAt || new Date().toISOString()),
        updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString())
      } as TripChecklistItem];
    }));
  } catch {
    return [];
  }
}

function persistTripChecklist(uid: string, tripId: string, items: TripChecklistItem[]): TripChecklistItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripChecklist(uid, tripId);
  const sorted = sortTripChecklistItems(items);
  localStorage.setItem(tripChecklistKey(uid, tripId), JSON.stringify(sorted));
  scheduleTripVersionSnapshot(uid, tripId);
  return sorted;
}

export function saveTripChecklistItem(
  uid: string,
  tripId: string,
  draft: Pick<TripChecklistItem, "checklistId" | "title" | "category" | "notes" | "date" | "completed">,
  id?: string
): TripChecklistItem[] {
  const now = new Date().toISOString();
  const current = listTripChecklist(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : undefined;
  const item: TripChecklistItem = {
    ...draft,
    checklistId: draft.checklistId || DEFAULT_CHECKLIST_ID,
    id: existing?.id || (crypto.randomUUID ? crypto.randomUUID() : `check_${Date.now()}`),
    createdAt: existing?.createdAt || now,
    updatedAt: now
  };
  return persistTripChecklist(uid, tripId, [item, ...current.filter((entry) => entry.id !== item.id)]);
}

export function toggleTripChecklistItem(uid: string, tripId: string, id: string): TripChecklistItem[] {
  const now = new Date().toISOString();
  return persistTripChecklist(
    uid,
    tripId,
    listTripChecklist(uid, tripId).map((item) => item.id === id ? { ...item, completed: !item.completed, updatedAt: now } : item)
  );
}

export function deleteTripChecklistItem(uid: string, tripId: string, id: string): TripChecklistItem[] {
  return persistTripChecklist(uid, tripId, listTripChecklist(uid, tripId).filter((item) => item.id !== id));
}

export type TripNoteAlignment = "left" | "center" | "right" | "justify";

export interface TripNoteItem {
  id: string;
  title: string;
  content: string;
  icon: string;
  color: string;
  alignment: TripNoteAlignment;
  createdAt: string;
  updatedAt: string;
}

function tripNotesKey(uid: string, tripId: string): string {
  return `tripai.tripNotes.v1.${uid}.${tripId}`;
}

export function listTripNotes(uid: string, tripId: string): TripNoteItem[] {
  try {
    const raw = localStorage.getItem(tripNotesKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const item = value as Partial<TripNoteItem>;
      if (!item.id) return [];
      const alignment: TripNoteAlignment = item.alignment === "center" || item.alignment === "right" || item.alignment === "justify" ? item.alignment : "left";
      return [{
        id: String(item.id),
        title: String(item.title || ""),
        content: String(item.content || ""),
        icon: typeof item.icon === "string" ? item.icon : "📝",
        color: String(item.color || "#124848"),
        alignment,
        createdAt: String(item.createdAt || new Date().toISOString()),
        updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString())
      } as TripNoteItem];
    }).sort((a,b) => b.updatedAt.localeCompare(a.updatedAt));
  } catch { return []; }
}

function persistTripNotes(uid: string, tripId: string, items: TripNoteItem[]): TripNoteItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripNotes(uid, tripId);
  localStorage.setItem(tripNotesKey(uid, tripId), JSON.stringify(items));
  scheduleTripVersionSnapshot(uid, tripId);
  return items;
}

export function saveTripNote(
  uid: string,
  tripId: string,
  draft: Pick<TripNoteItem, "title" | "content" | "icon" | "color" | "alignment">,
  id?: string
): TripNoteItem[] {
  const now = new Date().toISOString();
  const current = listTripNotes(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : undefined;
  const item: TripNoteItem = {
    id: existing?.id || (crypto.randomUUID ? crypto.randomUUID() : `note_${Date.now()}`),
    title: draft.title.trim(),
    content: draft.content.trim(),
    icon: typeof draft.icon === "string" ? draft.icon : "📝",
    color: draft.color || "#124848",
    alignment: draft.alignment || "left",
    createdAt: existing?.createdAt || now,
    updatedAt: now
  };
  return persistTripNotes(uid, tripId, [item, ...current.filter((entry) => entry.id !== item.id)]);
}

export function deleteTripNote(uid: string, tripId: string, id: string): TripNoteItem[] {
  return persistTripNotes(uid, tripId, listTripNotes(uid, tripId).filter((item) => item.id !== id));
}

export type TripItineraryType = "activity" | "transfer" | "meal" | "free" | "other";

export interface TripItineraryItem {
  id: string;
  title: string;
  date: string;
  endDate: string;
  startTime: string;
  endTime: string;
  city: string;
  address: string;
  endCity?: string;
  endAddress?: string;
  latitude?: number;
  longitude?: number;
  endLatitude?: number;
  endLongitude?: number;
  transportModeId?: string;
  type: TripItineraryType;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

function tripItineraryKey(uid: string, tripId: string): string {
  return `tripai.tripItinerary.v1.${uid}.${tripId}`;
}

function sortTripItinerary(items: TripItineraryItem[]): TripItineraryItem[] {
  return [...items].sort((a, b) => {
    if (a.date !== b.date) return (a.date || "9999-99-99").localeCompare(b.date || "9999-99-99");
    if (a.startTime !== b.startTime) return (a.startTime || "99:99").localeCompare(b.startTime || "99:99");
    return a.title.localeCompare(b.title);
  });
}

export function listTripItinerary(uid: string, tripId: string): TripItineraryItem[] {
  try {
    const raw = localStorage.getItem(tripItineraryKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return sortTripItinerary(parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const item = value as Partial<TripItineraryItem>;
      if (!item.id || !item.title) return [];
      const type: TripItineraryType = item.type === "transfer" || item.type === "meal" || item.type === "free" || item.type === "other" ? item.type : "activity";
      return [{
        id: String(item.id), title: String(item.title), date: String(item.date || ""), endDate: String(item.endDate || item.date || ""), startTime: String(item.startTime || ""), endTime: String(item.endTime || ""),
        city: String(item.city || ""), address: String(item.address || ""),
        endCity: String(item.endCity || "") || undefined, endAddress: String(item.endAddress || "") || undefined,
        latitude: typeof item.latitude === "number" && Number.isFinite(item.latitude) ? item.latitude : undefined,
        longitude: typeof item.longitude === "number" && Number.isFinite(item.longitude) ? item.longitude : undefined,
        endLatitude: typeof item.endLatitude === "number" && Number.isFinite(item.endLatitude) ? item.endLatitude : undefined,
        endLongitude: typeof item.endLongitude === "number" && Number.isFinite(item.endLongitude) ? item.endLongitude : undefined,
        transportModeId: String(item.transportModeId || "") || undefined,
        type, notes: String(item.notes || ""),
        createdAt: String(item.createdAt || new Date().toISOString()), updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString())
      } satisfies TripItineraryItem];
    }));
  } catch { return []; }
}

function persistTripItinerary(uid: string, tripId: string, items: TripItineraryItem[]): TripItineraryItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripItinerary(uid, tripId);
  const sorted = sortTripItinerary(items);
  localStorage.setItem(tripItineraryKey(uid, tripId), JSON.stringify(sorted));
  scheduleTripVersionSnapshot(uid, tripId);
  return sorted;
}

export function saveTripItineraryItem(uid: string, tripId: string, draft: Omit<TripItineraryItem, "id" | "createdAt" | "updatedAt">, id?: string): TripItineraryItem[] {
  const now = new Date().toISOString();
  const current = listTripItinerary(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : undefined;
  const item: TripItineraryItem = { ...draft, id: existing?.id || (crypto.randomUUID ? crypto.randomUUID() : `itin_${Date.now()}`), createdAt: existing?.createdAt || now, updatedAt: now };
  return persistTripItinerary(uid, tripId, [item, ...current.filter((entry) => entry.id !== item.id)]);
}

export function deleteTripItineraryItem(uid: string, tripId: string, id: string): TripItineraryItem[] {
  return persistTripItinerary(uid, tripId, listTripItinerary(uid, tripId).filter((item) => item.id !== id));
}

export interface TripExperienceItem {
  id: string;
  title: string;
  placeName: string;
  city: string;
  address: string;
  date: string;
  time: string;
  price: number | null;
  currency: string;
  notes: string;
  latitude?: number;
  longitude?: number;
  googlePlaceId?: string;
  createdAt: string;
  updatedAt: string;
}

function tripExperiencesKey(uid: string, tripId: string): string {
  return `tripai.tripExperiences.v1.${uid}.${tripId}`;
}

function sortTripExperiences(items: TripExperienceItem[]): TripExperienceItem[] {
  return [...items].sort((a, b) => {
    if (a.date !== b.date) return (a.date || "9999-99-99").localeCompare(b.date || "9999-99-99");
    if (a.time !== b.time) return (a.time || "99:99").localeCompare(b.time || "99:99");
    return a.title.localeCompare(b.title);
  });
}

export function listTripExperiences(uid: string, tripId: string): TripExperienceItem[] {
  try {
    const raw = localStorage.getItem(tripExperiencesKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return sortTripExperiences(parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const item = value as Partial<TripExperienceItem>;
      if (!item.id || !item.title) return [];
      const rawPrice = (value as { price?: unknown }).price;
      return [{
        id: String(item.id), title: String(item.title), placeName: String(item.placeName || item.city || ""), city: String(item.city || ""), address: String(item.address || ""), date: String(item.date || ""), time: String(item.time || ""),
        price: rawPrice === null || rawPrice === undefined || rawPrice === "" ? null : Number.isFinite(Number(rawPrice)) ? Number(rawPrice) : null, currency: String(item.currency || "EUR"), notes: String(item.notes || ""),
        latitude: Number.isFinite(Number(item.latitude)) ? Number(item.latitude) : undefined,
        longitude: Number.isFinite(Number(item.longitude)) ? Number(item.longitude) : undefined,
        googlePlaceId: String(item.googlePlaceId || "") || undefined,
        createdAt: String(item.createdAt || new Date().toISOString()), updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString())
      } satisfies TripExperienceItem];
    }));
  } catch { return []; }
}

function persistTripExperiences(uid: string, tripId: string, items: TripExperienceItem[]): TripExperienceItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripExperiences(uid, tripId);
  const sorted = sortTripExperiences(items);
  localStorage.setItem(tripExperiencesKey(uid, tripId), JSON.stringify(sorted));
  scheduleTripVersionSnapshot(uid, tripId);
  return sorted;
}

export function saveTripExperience(uid: string, tripId: string, draft: Omit<TripExperienceItem, "id" | "createdAt" | "updatedAt">, id?: string): TripExperienceItem[] {
  const now = new Date().toISOString();
  const current = listTripExperiences(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : undefined;
  const item: TripExperienceItem = { ...draft, id: existing?.id || (crypto.randomUUID ? crypto.randomUUID() : `exp_${Date.now()}`), createdAt: existing?.createdAt || now, updatedAt: now };
  return persistTripExperiences(uid, tripId, [item, ...current.filter((entry) => entry.id !== item.id)]);
}

export function deleteTripExperience(uid: string, tripId: string, id: string): TripExperienceItem[] {
  return persistTripExperiences(uid, tripId, listTripExperiences(uid, tripId).filter((item) => item.id !== id));
}

export interface TripEventItem {
  id: string;
  title: string;
  venue: string;
  city: string;
  address: string;
  date: string;
  startTime: string;
  endTime: string;
  category: string;
  price: number | null;
  currency: string;
  url: string;
  notes: string;
  latitude?: number;
  longitude?: number;
  googlePlaceId?: string;
  createdAt: string;
  updatedAt: string;
}

function tripEventsKey(uid: string, tripId: string): string {
  return `tripai.tripEvents.v1.${uid}.${tripId}`;
}

function sortTripEvents(items: TripEventItem[]): TripEventItem[] {
  return [...items].sort((a, b) => {
    if (a.date !== b.date) return (a.date || "9999-99-99").localeCompare(b.date || "9999-99-99");
    if (a.startTime !== b.startTime) return (a.startTime || "99:99").localeCompare(b.startTime || "99:99");
    return a.title.localeCompare(b.title);
  });
}

export function listTripEvents(uid: string, tripId: string): TripEventItem[] {
  try {
    const raw = localStorage.getItem(tripEventsKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return sortTripEvents(parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const item = value as Partial<TripEventItem>;
      if (!item.id || !item.title) return [];
      const rawPrice = (value as { price?: unknown }).price;
      return [{
        id: String(item.id),
        title: String(item.title),
        venue: String(item.venue || ""),
        city: String(item.city || ""),
        address: String(item.address || ""),
        date: String(item.date || ""),
        startTime: String(item.startTime || ""),
        endTime: String(item.endTime || ""),
        category: String(item.category || ""),
        price: rawPrice === null || rawPrice === undefined || rawPrice === "" ? null : Number.isFinite(Number(rawPrice)) ? Number(rawPrice) : null,
        currency: String(item.currency || "EUR"),
        url: String(item.url || ""),
        notes: String(item.notes || ""),
        latitude: Number.isFinite(Number(item.latitude)) ? Number(item.latitude) : undefined,
        longitude: Number.isFinite(Number(item.longitude)) ? Number(item.longitude) : undefined,
        googlePlaceId: String(item.googlePlaceId || "") || undefined,
        createdAt: String(item.createdAt || new Date().toISOString()),
        updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString())
      } satisfies TripEventItem];
    }));
  } catch {
    return [];
  }
}

function persistTripEvents(uid: string, tripId: string, items: TripEventItem[]): TripEventItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripEvents(uid, tripId);
  const sorted = sortTripEvents(items);
  localStorage.setItem(tripEventsKey(uid, tripId), JSON.stringify(sorted));
  scheduleTripVersionSnapshot(uid, tripId);
  return sorted;
}

export function saveTripEvent(
  uid: string,
  tripId: string,
  draft: Omit<TripEventItem, "id" | "createdAt" | "updatedAt">,
  id?: string
): TripEventItem[] {
  const now = new Date().toISOString();
  const current = listTripEvents(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : undefined;
  const item: TripEventItem = {
    ...draft,
    id: existing?.id || (crypto.randomUUID ? crypto.randomUUID() : `event_${Date.now()}`),
    createdAt: existing?.createdAt || now,
    updatedAt: now
  };
  return persistTripEvents(uid, tripId, [item, ...current.filter((entry) => entry.id !== item.id)]);
}

export function deleteTripEvent(uid: string, tripId: string, id: string): TripEventItem[] {
  return persistTripEvents(uid, tripId, listTripEvents(uid, tripId).filter((item) => item.id !== id));
}

export type TripParticipantRole = string;
export type TripParticipantStatus = "manual" | "pending" | "accepted" | "declined" | "external";

export interface TripParticipantItem {
  id: string;
  name: string;
  email: string;
  identifier: string;
  role: TripParticipantRole;
  notes: string;
  invitationId?: string;
  invitationStatus: TripParticipantStatus;
  targetUid?: string;
  createdAt: string;
  updatedAt: string;
}

function tripParticipantsKey(uid: string, tripId: string): string {
  return `tripai.tripParticipants.v1.${uid}.${tripId}`;
}

export function listTripParticipants(uid: string, tripId: string): TripParticipantItem[] {
  try {
    const raw = localStorage.getItem(tripParticipantsKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const item = value as Partial<TripParticipantItem>;
      if (!item.id || !item.name) return [];
      const role: TripParticipantRole = String(item.role || "traveller");
      const rawStatus = String(item.invitationStatus || "manual");
      const invitationStatus: TripParticipantStatus = rawStatus === "pending" || rawStatus === "accepted" || rawStatus === "declined" || rawStatus === "external" ? rawStatus : "manual";
      return [{
        id: String(item.id), name: String(item.name), email: String(item.email || ""), identifier: String(item.identifier || item.email || item.name || ""), role, notes: String(item.notes || ""),
        invitationId: String(item.invitationId || "") || undefined, invitationStatus, targetUid: String(item.targetUid || "") || undefined,
        createdAt: String(item.createdAt || new Date().toISOString()), updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString())
      } satisfies TripParticipantItem];
    }).sort((a, b) => a.name.localeCompare(b.name));
  } catch { return []; }
}

function persistTripParticipants(uid: string, tripId: string, items: TripParticipantItem[]): TripParticipantItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripParticipants(uid, tripId);
  const sorted = [...items].sort((a, b) => a.name.localeCompare(b.name));
  localStorage.setItem(tripParticipantsKey(uid, tripId), JSON.stringify(sorted));
  scheduleTripVersionSnapshot(uid, tripId);
  return sorted;
}

export function saveTripParticipant(uid: string, tripId: string, draft: Omit<TripParticipantItem, "id" | "createdAt" | "updatedAt">, id?: string): TripParticipantItem[] {
  const now = new Date().toISOString();
  const current = listTripParticipants(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : undefined;
  const item: TripParticipantItem = { ...draft, id: existing?.id || (crypto.randomUUID ? crypto.randomUUID() : `part_${Date.now()}`), createdAt: existing?.createdAt || now, updatedAt: now };
  return persistTripParticipants(uid, tripId, [item, ...current.filter((entry) => entry.id !== item.id)]);
}

export function deleteTripParticipant(uid: string, tripId: string, id: string): TripParticipantItem[] {
  return persistTripParticipants(uid, tripId, listTripParticipants(uid, tripId).filter((item) => item.id !== id));
}

export function syncTripParticipantInvitationStatuses(
  uid: string,
  tripId: string,
  statuses: Array<{ invitationId: string; status: TripParticipantStatus; targetUid?: string; targetEmail?: string; displayName?: string }>
): TripParticipantItem[] {
  const byInvitation = new Map(statuses.map((item) => [item.invitationId, item]));
  const next = listTripParticipants(uid, tripId).map((item) => {
    if (!item.invitationId) return item;
    const remote = byInvitation.get(item.invitationId);
    if (!remote) return item;
    return {
      ...item,
      invitationStatus: remote.status,
      targetUid: remote.targetUid || item.targetUid,
      email: remote.targetEmail || item.email,
      name: remote.displayName || item.name,
      updatedAt: new Date().toISOString()
    };
  });
  return persistTripParticipants(uid, tripId, next);
}


export interface TripPhotoItem {
  id: string;
  name: string;
  queueId: string;
  storagePath: string;
  url?: string;
  status: "pending" | "uploaded";
  date: string;
  city: string;
  sourceKind: string;
  sourceId: string;
  caption: string;
  folder?: string;
  createdAt: string;
  updatedAt: string;
}

function tripPhotosKey(uid: string, tripId: string): string {
  return `tripai.tripPhotos.v1.${uid}.${tripId}`;
}

function normalizeTripPhoto(value: unknown): TripPhotoItem | null {
  if (!value || typeof value !== "object") return null;
  const item = value as Partial<TripPhotoItem>;
  if (!item.id || !item.name || !item.queueId || !item.storagePath) return null;
  return {
    id: String(item.id),
    name: String(item.name),
    queueId: String(item.queueId),
    storagePath: String(item.storagePath),
    url: String(item.url || "") || undefined,
    status: item.status === "uploaded" && item.url ? "uploaded" : "pending",
    date: String(item.date || ""),
    city: String(item.city || ""),
    sourceKind: String(item.sourceKind || ""),
    sourceId: String(item.sourceId || ""),
    caption: String(item.caption || ""),
    folder: String(item.folder || "") || undefined,
    createdAt: String(item.createdAt || new Date().toISOString()),
    updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString())
  };
}

export function listTripPhotos(uid: string, tripId: string): TripPhotoItem[] {
  try {
    const raw = localStorage.getItem(tripPhotosKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizeTripPhoto)
      .filter((item): item is TripPhotoItem => Boolean(item))
      .sort((a, b) => `${a.date}|${a.createdAt}`.localeCompare(`${b.date}|${b.createdAt}`));
  } catch {
    return [];
  }
}

function persistTripPhotos(uid: string, tripId: string, items: TripPhotoItem[]): TripPhotoItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripPhotos(uid, tripId);
  const sorted = [...items].sort((a, b) => `${a.date}|${a.createdAt}`.localeCompare(`${b.date}|${b.createdAt}`));
  localStorage.setItem(tripPhotosKey(uid, tripId), JSON.stringify(sorted));
  scheduleTripVersionSnapshot(uid, tripId);
  return sorted;
}

export function saveTripPhoto(
  uid: string,
  tripId: string,
  input: Omit<TripPhotoItem, "createdAt" | "updatedAt" | "status" | "url"> & { url?: string },
  id?: string
): TripPhotoItem[] {
  const now = new Date().toISOString();
  const current = listTripPhotos(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : undefined;
  const item: TripPhotoItem = {
    ...input,
    id: existing?.id || input.id || (crypto.randomUUID ? crypto.randomUUID() : `photo_${Date.now()}`),
    url: input.url || existing?.url,
    status: input.url || existing?.url ? "uploaded" : "pending",
    createdAt: existing?.createdAt || now,
    updatedAt: now
  };
  return persistTripPhotos(uid, tripId, [item, ...current.filter((entry) => entry.id !== item.id)]);
}

export function markTripPhotoUploaded(uid: string, tripId: string, queueId: string, url: string): TripPhotoItem[] {
  const now = new Date().toISOString();
  return persistTripPhotos(uid, tripId, listTripPhotos(uid, tripId).map((item) => item.queueId === queueId
    ? { ...item, url, status: "uploaded" as const, updatedAt: now }
    : item));
}

export function deleteTripPhoto(uid: string, tripId: string, id: string): TripPhotoItem[] {
  return persistTripPhotos(uid, tripId, listTripPhotos(uid, tripId).filter((item) => item.id !== id));
}

export interface TripClimateDay {
  id: string;
  date: string;
  conditions: string;
  minTemp: string;
  maxTemp: string;
  summary: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

function tripClimateKey(uid: string, tripId: string): string {
  return `tripai.tripClimate.v1.${uid}.${tripId}`;
}

export function listTripClimate(uid: string, tripId: string): TripClimateDay[] {
  try {
    const raw = localStorage.getItem(tripClimateKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const item = value as Partial<TripClimateDay>;
      if (!item.id || !item.date) return [];
      return [{
        id: String(item.id), date: String(item.date), conditions: String(item.conditions || ""),
        minTemp: String(item.minTemp || ""), maxTemp: String(item.maxTemp || ""),
        summary: String(item.summary || ""), notes: String(item.notes || ""),
        createdAt: String(item.createdAt || new Date().toISOString()),
        updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString())
      } satisfies TripClimateDay];
    }).sort((a, b) => a.date.localeCompare(b.date));
  } catch {
    return [];
  }
}

function persistTripClimate(uid: string, tripId: string, items: TripClimateDay[]): TripClimateDay[] {
  if (blockArchivedMutation(uid, tripId)) return listTripClimate(uid, tripId);
  const sorted = [...items].sort((a, b) => a.date.localeCompare(b.date));
  localStorage.setItem(tripClimateKey(uid, tripId), JSON.stringify(sorted));
  return sorted;
}

export function saveTripClimateDay(
  uid: string,
  tripId: string,
  draft: Omit<TripClimateDay, "id" | "createdAt" | "updatedAt">,
  id?: string
): TripClimateDay[] {
  const now = new Date().toISOString();
  const current = listTripClimate(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : current.find((item) => item.date === draft.date);
  const item: TripClimateDay = {
    ...draft,
    id: existing?.id || (crypto.randomUUID ? crypto.randomUUID() : `climate_${Date.now()}`),
    createdAt: existing?.createdAt || now,
    updatedAt: now
  };
  return persistTripClimate(uid, tripId, [item, ...current.filter((entry) => entry.id !== item.id)]);
}

export function deleteTripClimateDay(uid: string, tripId: string, id: string): TripClimateDay[] {
  return persistTripClimate(uid, tripId, listTripClimate(uid, tripId).filter((item) => item.id !== id));
}


export interface TripAiChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
}

function tripAiChatKey(uid: string, tripId: string): string {
  return `tripai.tripAiChat.v1.${uid}.${tripId}`;
}

export function loadTripAiChatMessages(uid: string, tripId: string): TripAiChatMessage[] {
  try {
    const raw = localStorage.getItem(tripAiChatKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((value) => {
      if (!value || typeof value !== "object") return [];
      const item = value as Partial<TripAiChatMessage>;
      const role = item.role === "assistant" ? "assistant" : item.role === "user" ? "user" : null;
      const content = String(item.content || "").trim();
      if (!role || !content) return [];
      return [{
        id: String(item.id || `ai_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`),
        role,
        content,
        createdAt: String(item.createdAt || new Date().toISOString())
      } satisfies TripAiChatMessage];
    }).slice(-80);
  } catch {
    return [];
  }
}

export function saveTripAiChatMessages(uid: string, tripId: string, messages: TripAiChatMessage[]): TripAiChatMessage[] {
  if (blockArchivedMutation(uid, tripId)) return loadTripAiChatMessages(uid, tripId);
  const normalized = messages.flatMap((item) => {
    const content = String(item.content || "").trim();
    if (!content || (item.role !== "user" && item.role !== "assistant")) return [];
    return [{
      id: String(item.id || `ai_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`),
      role: item.role,
      content: content.slice(0, 12000),
      createdAt: String(item.createdAt || new Date().toISOString())
    } satisfies TripAiChatMessage];
  }).slice(-80);
  localStorage.setItem(tripAiChatKey(uid, tripId), JSON.stringify(normalized));
  return normalized;
}


export type TripExpenseSplitMode = "equal" | "percentage" | "shares";

export interface TripExpenseSplitItem {
  participantId: string;
  participantName: string;
  value: number;
  amount: number;
}

export interface TripExpenseItem {
  id: string;
  title: string;
  amount: number;
  currency: string;
  category: "transport" | "stay" | "food" | "activities" | "shopping" | "fees" | "other";
  date: string;
  note?: string;
  payer?: string;
  payerId?: string;
  splitMode?: TripExpenseSplitMode;
  split?: TripExpenseSplitItem[];
  createdAt: string;
  updatedAt: string;
}

function tripExpensesKey(uid: string, tripId: string): string {
  return `tripai.tripExpenses.v1.${uid}.${tripId}`;
}

function normalizeTripExpense(value: unknown): TripExpenseItem | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<TripExpenseItem>;
  const id = String(raw.id || "").trim();
  const title = String(raw.title || "").trim();
  const amount = Number(raw.amount);
  if (!id || !title || !Number.isFinite(amount) || amount <= 0) return null;
  const category = raw.category === "transport" || raw.category === "stay" || raw.category === "food" || raw.category === "activities" || raw.category === "shopping" || raw.category === "fees" ? raw.category : "other";
  const splitMode: TripExpenseSplitMode | undefined = raw.splitMode === "percentage" || raw.splitMode === "shares" || raw.splitMode === "equal" ? raw.splitMode : undefined;
  const split = Array.isArray(raw.split) ? raw.split.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const candidate = entry as Partial<TripExpenseSplitItem>;
    const participantId = String(candidate.participantId || "").trim();
    const participantName = String(candidate.participantName || "").trim();
    const value = Number(candidate.value || 0);
    const splitAmount = Number(candidate.amount || 0);
    if (!participantId || !participantName || !Number.isFinite(value) || !Number.isFinite(splitAmount)) return [];
    return [{ participantId, participantName, value, amount: roundCurrency(splitAmount, String(raw.currency || "EUR").toUpperCase()) } satisfies TripExpenseSplitItem];
  }) : undefined;
  return {
    id, title, amount, currency: String(raw.currency || "EUR").toUpperCase(), category,
    date: String(raw.date || ""), note: String(raw.note || "") || undefined, payer: String(raw.payer || "") || undefined,
    payerId: String(raw.payerId || "") || undefined, splitMode, split,
    createdAt: String(raw.createdAt || new Date().toISOString()), updatedAt: String(raw.updatedAt || raw.createdAt || new Date().toISOString())
  };
}

export function listTripExpenses(uid: string, tripId: string): TripExpenseItem[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(tripExpensesKey(uid, tripId)) || "[]") as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeTripExpense).filter((item): item is TripExpenseItem => Boolean(item)).sort((a,b) => (b.date || b.updatedAt).localeCompare(a.date || a.updatedAt));
  } catch { return []; }
}

function persistTripExpenses(uid: string, tripId: string, items: TripExpenseItem[]): TripExpenseItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripExpenses(uid, tripId);
  localStorage.setItem(tripExpensesKey(uid, tripId), JSON.stringify(items));
  scheduleTripVersionSnapshot(uid, tripId);
  return items;
}

export function saveTripExpense(uid: string, tripId: string, draft: Omit<TripExpenseItem, "id" | "createdAt" | "updatedAt">, id?: string): TripExpenseItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripExpenses(uid, tripId);
  const now = new Date().toISOString();
  const current = listTripExpenses(uid, tripId);
  const existing = id ? current.find((item) => item.id === id) : undefined;
  const nextItem: TripExpenseItem = { ...draft, amount: roundCurrency(draft.amount, draft.currency), ...(draft.split?.length ? { split: computeSplit(draft.amount, draft.splitMode || "equal", draft.split, draft.currency) } : {}), id: existing?.id || id || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `expense_${Date.now()}_${Math.random().toString(36).slice(2,8)}`), createdAt: existing?.createdAt || now, updatedAt: now };
  const next = persistTripExpenses(uid, tripId, [nextItem, ...current.filter((item) => item.id !== nextItem.id)]);
  void queueExpenseForFainanceShareSync(uid, {
    tripId, expenseId: nextItem.id, amount: nextItem.amount, description: nextItem.title, category: nextItem.category,
    currency: nextItem.currency, date: nextItem.date, payer: nextItem.payer, payerId: nextItem.payerId, splitMode: nextItem.splitMode, split: nextItem.split, deleted: false
  }).catch(() => false);
  return next;
}

export function deleteTripExpense(uid: string, tripId: string, id: string): TripExpenseItem[] {
  if (blockArchivedMutation(uid, tripId)) return listTripExpenses(uid, tripId);
  const current = listTripExpenses(uid, tripId);
  const existing = current.find((item) => item.id === id);
  const next = persistTripExpenses(uid, tripId, current.filter((item) => item.id !== id));
  void queueExpenseForFainanceShareSync(uid, {
    tripId, expenseId: id, amount: existing?.amount || 0, description: existing?.title || "", category: existing?.category || "other",
    currency: existing?.currency || "EUR", date: existing?.date || "", deleted: true
  }).catch(() => false);
  return next;
}

export type TripWorkspaceSectionId =
  | "usefulInfo"
  | "boardingPasses"
  | "tickets"
  | "documents"
  | "hotels"
  | "restaurants"
  | "places"
  | "pointsOfInterest"
  | "checklists"
  | "notes"
  | "itinerary"
  | "experiences"
  | "events"
  | "participants"
  | "photos"
  | "climate"
  | "expenses"
  | "aiChat";

export const tripWorkspaceSectionIds: TripWorkspaceSectionId[] = [
  "usefulInfo",
  "boardingPasses",
  "tickets",
  "documents",
  "hotels",
  "restaurants",
  "places",
  "pointsOfInterest",
  "checklists",
  "notes",
  "itinerary",
  "experiences",
  "events",
  "participants",
  "photos",
  "climate",
  "expenses",
  "aiChat"
];

export function hasLocalTripWorkspaceSection(uid: string, tripId: string, section: TripWorkspaceSectionId): boolean {
  try {
    if (section === "usefulInfo") return localStorage.getItem(usefulInfoKey(uid, tripId)) !== null;
    if (section === "boardingPasses") return localStorage.getItem(boardingPassKey(uid, tripId)) !== null;
    if (section === "tickets") return localStorage.getItem(tripTicketsKey(uid, tripId)) !== null;
    if (section === "documents") return localStorage.getItem(tripDocumentFoldersKey(uid, tripId)) !== null || localStorage.getItem(tripDocumentsKey(uid, tripId)) !== null;
    if (section === "hotels") return localStorage.getItem(tripHotelsKey(uid, tripId)) !== null;
    if (section === "restaurants") return localStorage.getItem(tripRestaurantsKey(uid, tripId)) !== null;
    if (section === "places") return localStorage.getItem(tripPlacesKey(uid, tripId)) !== null;
    if (section === "pointsOfInterest") return localStorage.getItem(tripPointsOfInterestKey(uid, tripId)) !== null;
    if (section === "checklists") return localStorage.getItem(tripChecklistListsKey(uid, tripId)) !== null || localStorage.getItem(tripChecklistKey(uid, tripId)) !== null || localStorage.getItem(smartPreparationKey(uid, tripId)) !== null || localStorage.getItem(guidedPlanKey(uid, tripId)) !== null || localStorage.getItem(tripSetupKey(uid, tripId)) !== null;
    if (section === "notes") return localStorage.getItem(tripNotesKey(uid, tripId)) !== null;
    if (section === "itinerary") return localStorage.getItem(tripItineraryKey(uid, tripId)) !== null;
    if (section === "experiences") return localStorage.getItem(tripExperiencesKey(uid, tripId)) !== null;
    if (section === "events") return localStorage.getItem(tripEventsKey(uid, tripId)) !== null;
    if (section === "participants") return localStorage.getItem(tripParticipantsKey(uid, tripId)) !== null;
    if (section === "photos") return localStorage.getItem(tripPhotosKey(uid, tripId)) !== null;
    if (section === "climate") return localStorage.getItem(tripClimateKey(uid, tripId)) !== null;
    if (section === "expenses") return localStorage.getItem(tripExpensesKey(uid, tripId)) !== null;
    if (section === "aiChat") return localStorage.getItem(tripAiChatKey(uid, tripId)) !== null;
    return false;
  } catch {
    return false;
  }
}

export function exportTripWorkspaceSection(uid: string, tripId: string, section: TripWorkspaceSectionId): unknown {
  if (section === "usefulInfo") return loadTripUsefulInfo(uid, tripId);
  if (section === "boardingPasses") return listBoardingPasses(uid, tripId);
  if (section === "tickets") return listTripTickets(uid, tripId);
  if (section === "documents") return { folders: listTripDocumentFolders(uid, tripId), items: listTripDocuments(uid, tripId) };
  if (section === "hotels") return listTripHotels(uid, tripId);
  if (section === "restaurants") return listTripRestaurants(uid, tripId);
  if (section === "places") return listTripPlaces(uid, tripId);
  if (section === "pointsOfInterest") return listTripPointsOfInterest(uid, tripId);
  if (section === "checklists") return { lists: listTripChecklistLists(uid, tripId), items: listTripChecklist(uid, tripId), smartPreparation: listSmartPreparationStatuses(uid, tripId), guidedPlan: listGuidedPlan(uid, tripId), tripSetup: listTripSetup(uid, tripId) };
  if (section === "notes") return listTripNotes(uid, tripId);
  if (section === "itinerary") return listTripItinerary(uid, tripId);
  if (section === "experiences") return listTripExperiences(uid, tripId);
  if (section === "events") return listTripEvents(uid, tripId);
  if (section === "participants") return listTripParticipants(uid, tripId);
  if (section === "photos") return listTripPhotos(uid, tripId);
  if (section === "climate") return listTripClimate(uid, tripId);
  if (section === "expenses") return listTripExpenses(uid, tripId);
  if (section === "aiChat") return loadTripAiChatMessages(uid, tripId);
  return null;
}

export function exportTripWorkspace(uid: string, tripId: string): Record<TripWorkspaceSectionId, unknown> {
  return tripWorkspaceSectionIds.reduce((result, section) => {
    result[section] = exportTripWorkspaceSection(uid, tripId, section);
    return result;
  }, {} as Record<TripWorkspaceSectionId, unknown>);
}

export function applyTripWorkspaceSection(uid: string, tripId: string, section: TripWorkspaceSectionId, data: unknown): void {
  try {
    if (section === "usefulInfo") localStorage.setItem(usefulInfoKey(uid, tripId), JSON.stringify(data || createEmptyUsefulInfo()));
    else if (section === "boardingPasses") localStorage.setItem(boardingPassKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "tickets") localStorage.setItem(tripTicketsKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "documents") {
      const value = (data && typeof data === "object" ? data : {}) as { folders?: unknown; items?: unknown };
      localStorage.setItem(tripDocumentFoldersKey(uid, tripId), JSON.stringify(Array.isArray(value.folders) ? value.folders : []));
      localStorage.setItem(tripDocumentsKey(uid, tripId), JSON.stringify(Array.isArray(value.items) ? value.items : []));
    } else if (section === "hotels") localStorage.setItem(tripHotelsKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "restaurants") localStorage.setItem(tripRestaurantsKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "places") localStorage.setItem(tripPlacesKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "pointsOfInterest") localStorage.setItem(tripPointsOfInterestKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "checklists") {
      const value = (data && typeof data === "object" ? data : {}) as { lists?: unknown; items?: unknown; smartPreparation?: unknown; guidedPlan?: unknown; tripSetup?: unknown };
      localStorage.setItem(tripChecklistListsKey(uid, tripId), JSON.stringify(Array.isArray(value.lists) ? value.lists : []));
      localStorage.setItem(tripChecklistKey(uid, tripId), JSON.stringify(Array.isArray(value.items) ? value.items : []));
      // Older devices may not know this field. Do not discard local confirmations
      // when hydrating a legacy workspace section without smartPreparation.
      if (value.smartPreparation && typeof value.smartPreparation === "object" && !Array.isArray(value.smartPreparation)) {
        const statuses = Object.fromEntries(Object.entries(value.smartPreparation).filter(([key, status]) =>
          /^[a-zA-Z]{3,32}$/.test(key) && (status === "confirmed" || status === "notApplicable")
        ));
        localStorage.setItem(smartPreparationKey(uid, tripId), JSON.stringify(statuses));
      }
      // Legacy snapshots must not erase a locally edited plan.
      if (value.guidedPlan !== undefined && value.guidedPlan !== null) {
        const plan = normalizeGuidedPlan(value.guidedPlan);
        if (plan) localStorage.setItem(guidedPlanKey(uid, tripId), JSON.stringify(plan));
      }
      if (value.tripSetup !== undefined && value.tripSetup !== null) {
        const setup = normalizeTripSetup(value.tripSetup);
        if (setup) localStorage.setItem(tripSetupKey(uid, tripId), JSON.stringify(setup));
      }
    } else if (section === "notes") localStorage.setItem(tripNotesKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "itinerary") localStorage.setItem(tripItineraryKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "experiences") localStorage.setItem(tripExperiencesKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "events") localStorage.setItem(tripEventsKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "participants") localStorage.setItem(tripParticipantsKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "photos") localStorage.setItem(tripPhotosKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "climate") localStorage.setItem(tripClimateKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "expenses") localStorage.setItem(tripExpensesKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
    else if (section === "aiChat") localStorage.setItem(tripAiChatKey(uid, tripId), JSON.stringify(Array.isArray(data) ? data : []));
  } catch {
    // Remote synchronization is best-effort; local data remains available.
  }
}

export interface TripVersionSnapshot {
  id: string;
  tripId: string;
  createdAt: string;
  summary: TripSummary;
  workspace: Partial<Record<TripWorkspaceSectionId, unknown>>;
  signature: string;
}

const TRIP_VERSION_RETENTION = 12;
const versionSnapshotTimers = new Map<string, number>();
const versionedWorkspaceSections: TripWorkspaceSectionId[] = [
  "usefulInfo",
  "boardingPasses",
  "documents",
  "hotels",
  "restaurants",
  "places",
  "pointsOfInterest",
  "checklists",
  "notes",
  "itinerary",
  "experiences",
  "events",
  "participants",
  "expenses"
];

function tripVersionHistoryKey(uid: string, tripId: string): string {
  return `mundely.tripVersions.v1.${uid}.${tripId}`;
}

function tripVersionTimerKey(uid: string, tripId: string): string {
  return `${uid}:${tripId}`;
}

function tripVersionSignature(summary: TripSummary, workspace: Partial<Record<TripWorkspaceSectionId, unknown>>): string {
  const normalizedSummary = {
    name: summary.name,
    destination: summary.destination,
    startDate: summary.startDate,
    endDate: summary.endDate,
    status: summary.status,
    profilePhotoGooglePlaceId: summary.profilePhotoGooglePlaceId || "",
    profilePhotoIndex: summary.profilePhotoIndex ?? null,
    coverPhotoGooglePlaceId: summary.coverPhotoGooglePlaceId || "",
    coverPhotoIndex: summary.coverPhotoIndex ?? null
  };
  return JSON.stringify({ summary: normalizedSummary, workspace });
}

export function listTripVersions(uid: string, tripId: string): TripVersionSnapshot[] {
  try {
    const raw = localStorage.getItem(tripVersionHistoryKey(uid, tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is TripVersionSnapshot => Boolean(item && typeof item === "object" && (item as TripVersionSnapshot).id))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .slice(0, TRIP_VERSION_RETENTION);
  } catch {
    return [];
  }
}

export function captureTripVersion(uid: string, tripId: string): TripVersionSnapshot | null {
  try {
    const summary = readTripsAll(uid).find((trip) => trip.id === tripId && !trip.deletedAt);
    if (!summary) return null;
    const workspace: Partial<Record<TripWorkspaceSectionId, unknown>> = {};
    for (const section of versionedWorkspaceSections) {
      if (!hasLocalTripWorkspaceSection(uid, tripId, section)) continue;
      workspace[section] = exportTripWorkspaceSection(uid, tripId, section);
    }
    const signature = tripVersionSignature(summary, workspace);
    const current = listTripVersions(uid, tripId);
    if (current[0]?.signature === signature) return current[0];
    const snapshot: TripVersionSnapshot = {
      id: typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `version_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      tripId,
      createdAt: new Date().toISOString(),
      summary: { ...summary },
      workspace,
      signature
    };
    localStorage.setItem(tripVersionHistoryKey(uid, tripId), JSON.stringify([snapshot, ...current].slice(0, TRIP_VERSION_RETENTION)));
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("mundely:trip-versions-updated", { detail: { uid, tripId } }));
    }
    return snapshot;
  } catch {
    return null;
  }
}

export function scheduleTripVersionSnapshot(uid: string, tripId: string): void {
  if (typeof window === "undefined" || !uid || !tripId) return;
  window.dispatchEvent(new CustomEvent("tripai:workspace-updated", { detail: { uid, tripId } }));
  const key = tripVersionTimerKey(uid, tripId);
  const previous = versionSnapshotTimers.get(key);
  if (previous) window.clearTimeout(previous);
  const timer = window.setTimeout(() => {
    versionSnapshotTimers.delete(key);
    captureTripVersion(uid, tripId);
  }, 900);
  versionSnapshotTimers.set(key, timer);
}

export function restoreTripVersion(uid: string, snapshot: TripVersionSnapshot): TripSummary | null {
  const current = readTripsAll(uid).find((trip) => trip.id === snapshot.tripId);
  if (!current || current.deletedAt) return null;
  captureTripVersion(uid, snapshot.tripId);
  const restoredSummary = updateTrip(uid, snapshot.tripId, {
    name: snapshot.summary.name,
    destination: snapshot.summary.destination,
    startDate: snapshot.summary.startDate,
    endDate: snapshot.summary.endDate,
    status: snapshot.summary.status,
    profilePhotoGooglePlaceId: snapshot.summary.profilePhotoGooglePlaceId,
    profilePhotoIndex: snapshot.summary.profilePhotoIndex,
    profilePhotoDataUrl: snapshot.summary.profilePhotoDataUrl,
    coverPhotoGooglePlaceId: snapshot.summary.coverPhotoGooglePlaceId,
    coverPhotoIndex: snapshot.summary.coverPhotoIndex,
    coverPhotoDataUrl: snapshot.summary.coverPhotoDataUrl,
    aiPhotosSelectedAt: snapshot.summary.aiPhotosSelectedAt,
    archivedAt: snapshot.summary.archivedAt
  });
  for (const section of versionedWorkspaceSections) {
    if (!(section in snapshot.workspace)) continue;
    applyTripWorkspaceSection(uid, snapshot.tripId, section, snapshot.workspace[section]);
  }
  emitTripsUpdated(uid);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("mundely:trip-version-restored", { detail: { uid, tripId: snapshot.tripId } }));
  }
  return restoredSummary;
}

